import { Injectable } from "@angular/core";
import { PRIMITIVE, ServiceMessageBrokerFactory } from "shared/provider-webworker";

import { ModelUpdate } from "../../impl/ModelUpdate";
import { PrimaryBlotterLookupService } from "../../impl/PrimaryBlotterLookupService";
import { IBenchmarkDataPromiseLookupService } from "../IBenchmarkDataPromiseLookupService";

import { Subject, Observable, Subscription, ReplaySubject } from "rxjs";

import { CubiqWorker } from "../../../worker/CubiqWorker";
import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { RawDataBmarkClientOrdersAdapter } from "../../../mediator/adapter/RawDataBmarkClientOrdersAdapter";
import { OrderDataPrivatizationService } from "../../../model/privatization/OrderDataPrivatizationService";
import { SysFeatures } from "../../../model/core/SysFeatures";
import { UserLookupService } from "./UserLookupService";
import { UserImpl } from "shared/cubiq-model-multi-asset";
import { TraderGroupLookupService } from "./TraderGroupLookupService";
import { TradingGroupImpl } from "shared/cubiq-model-multi-asset";
import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { ActiveUser } from "../../../model/core/ActiveUser";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { BmarkFixingData } from "../../../model/benchmarks/BmarkFixingData.model";
import { BmarkFixingDefinition } from "../../../model/benchmarks/BmarkFixingDefinition.model";
import { BenchmarkDataLookupService } from "./BenchmarkDataLookupService";
import { BmarkFixingType } from "../../../model/benchmarks/enums/BmarkFixingType.enum";
import { BmarkFixingEventOperation } from "../../../model/benchmarks/enums/BmarkFixingEventOperation.enum";
import { BmarkOrderDataEventOperation } from "../../../model/benchmarks/enums/BmarkOrderDataEventOperation.enum";
import { SystemConfigPromiseService } from "shared/auth-services";
import { filter } from "rxjs/operators";
import { NotificationEventProcessorService } from "../../../model/notifications/NotificationEventProcessorService";
import { BmarkOrderDataEventSource } from "shared/cubiq-services/model/benchmarks/enums/BmarkOrderDataEventSource.enum";
import { BmarkAllocationMetaData } from "shared/cubiq-services/model/benchmarks/BmarkAllocationMetaData.model";
import { NotificationService } from "../../notification/impl/NotificationService";
import { BenchmarkFixingPrimaryLookupService } from "./BenchmarkFixingPrimaryLookupService";
import { BmarkOrderData } from "shared/cubiq-services/model/benchmarks/BmarkOrderData.model";
import {
  BmarkOrderDataEvent,
  BmarkOrderDataEventPayload,
} from "../../../model/benchmarks/BmarkOrderDataEvent.model";
import { BmarkFixingEvent } from "../../../model/benchmarks/BmarkFixingEvent.model";
import { OrderDataModelUpdateSource } from "./OrderDataPromiseLookupService";

// Notifications
import { BFXNotificationType } from "../../../model/core/notifications/BFXNotificationType";
import { BFXAppNotificationType } from "../../../model/core/notifications/BFXAppNotificationType";
import { BFXNotificationSubType } from "../../../model/core/notifications/BFXNotificationSubType";
import { BFXAppNotificationSubType } from "../../../model/core/notifications/BFXAppNotificationSubType";
// event types
import { BFXNotificationEventType } from "shared/cubiq-services/model/core/notifications/BFXNotificationEventType";
import { BFXAppNotificationEventType } from "shared/cubiq-services/model/core/notifications/BFXAppNotificationEventType";
import {
  AllBenchmarksRequestService,
  AllBenchmarksResponse,
} from "shared/cubiq-services/action/impl/AllBenchmarksRequestService";
import { AllBenchmarksFixingStatusService } from "shared/cubiq-services/action/impl/AllBenchmarksFixingStatusService";
import { AllBenchmarksFixingSummariesService } from "shared/cubiq-services/action/impl/AllBenchmarksFixingSummariesService";
import { BfxConfigService } from "shared/auth-services/system-config/bfx-config.service";
import { BENCHMARK_ACTIVE_STATES, BENCHMARK_REJECT_STATES } from "../../../../auth-services";
import { BENCHMARK_DEAL_FAILURE_STATE } from "../../../model/benchmarks/BmarkDealFailureState.const";
import { UnsolicitedCancelLifeCycleEventImpl } from "shared/cubiq-model-multi-asset/model/impl/UnsolicitedCancelLifeCycleEventImpl";

import {
  TRANSFERED_OUT_OF_VISIBILITY,
  TransferUtils,
} from "shared/cubiq-services/model/orders/TransferUtils";
import { BMRiskUtils } from "shared/cubiq-services/model/benchmarks/helpers/BMRiskUtils";
import { DealPrivatizationService } from "shared/cubiq-services/model/privatization/DealPrivatizationService";

export const BLOTTERS_ACTIVE_BENCHMARKS: string[] = ["Active Benchmarks"];
export const BLOTTERS_ALL_BENCHMARKS: string[] = ["All Benchmarks"];

export const PRIMING_BATCH_SIZE: number = 100;

export const BENCHMARK_NEW_ORDER_STATES: Set<CubiqTS.OrderState> = new Set([
  CubiqTS.OrderState.DI_NEEDED,
]);

class QueuedEvent {
  constructor(
    public event: any,
    public processEventFunction: any,
  ) {}
}

const EXTINFO_SAME_TIME_PROCESSING = "SAME_TIME_PROCESSING";
const BOOLEAN_STR_TRUE = "TRUE";
@Injectable()
export class BenchmarkDataPrimaryLookupService
  extends PrimaryBlotterLookupService<string, CubiqTS.OrderDataImpl>
  implements IBenchmarkDataPromiseLookupService
{
  private activeUser: ActiveUser;
  // Message ports for Replica
  protected bmarkUpdateEventPorts: Array<MessagePort> = new Array<MessagePort>();
  // Primary subjects
  protected bmarkFixingsUpdateSubject: Subject<any> = new Subject<any>();
  protected bmarkOrderDataUpdateSubject: Subject<BmarkOrderDataEvent> = new Subject();
  // neutral subjects
  protected interventionEventSubject: Subject<CubiqTS.InterventionEventImpl> =
    new Subject<CubiqTS.InterventionEventImpl>();

  protected fixingModelUpdateSubscription: Subscription = null;
  public fixingModelUpdateSubject: Subject<CubiqTS.BenchmarkFixingImpl> =
    new Subject<CubiqTS.BenchmarkFixingImpl>();
  // Fixings Purge configs
  protected fixingsRecentOrdersIdleOffset: number = 30 * 1000; // 30 secs
  protected fixingsRecentPurgeInterval: number = 30 * 1000; // 30 secs
  protected fixingsRecentPurgeOffset: number = 168 * 3600 * 1000; // 168 hours
  protected defaultSweepOffset: number = 10 * 60 * 1000; // just a fallback, 10 mins
  // Info needed for the recent purge
  protected displayedRecentFixing: string = null;
  // Needed for Fixing expiration
  protected fixingDefinitionMap: Map<string, BmarkFixingDefinition> = new Map<
    string,
    BmarkFixingDefinition
  >();
  //
  protected fixingNotificationsLastSentStateMap: Map<string, BFXNotificationType> = new Map<
    string,
    BFXNotificationType
  >();
  // Bmark Order Data UPDATE Event internal configs
  protected control: {
    queueFixingEvents: boolean;
    queueOrderDataEvents: boolean;
    attachImplToEvent: boolean; // controls attachment of OrderDataImpl to appropriate events
  } = {
    queueFixingEvents: true,
    queueOrderDataEvents: true,
    attachImplToEvent: false,
  };

  private fixingEventQueue: Array<any> = new Array<any>();
  private orderDataEventQueue: Array<any> = new Array<any>();

  private outputLog: boolean = true; // for dev testing - to be deleted at some point

  private retrievalRecentFixingId: string | null = null;
  private retrievalRecentProcessingQueue: Set<string> = new Set<string>();
  private retrievalRecentFixingTimestamp: number | null = null;
  private retrievalRecentFixingProcessTimeOffset: number = 60 * 1000; // 1 min

  private configurationSubscription: Subscription;

  public fixingStatusResponseSubject: Subject<CubiqTS.FixingStatusResponseImpl> = new ReplaySubject(
    1,
  );

  public recentFixingSubject: Subject<Array<CubiqTS.BMTimeCodeImpl>> = new ReplaySubject(1);

  public autoHedgeSubject: Subject<CubiqTS.AutoHedgingStatusEventImpl> = new Subject();
  private recentFixingsSubscription: Subscription;

  constructor(
    protected base: BenchmarkDataLookupService,
    protected factory: ServiceMessageBrokerFactory,
    protected cubiqWorker: CubiqWorker,
    protected userService: UserLookupService,
    protected traderGroupService: TraderGroupLookupService,
    protected dataAdapter: RawDataBmarkClientOrdersAdapter,
    private orderPrivatizationService: OrderDataPrivatizationService,
    private dealPrivatizationService: DealPrivatizationService,
    private activeUserService: ActiveUserProfilePromiseService,
    private sysConfigService: SystemConfigPromiseService,
    private fixingDefinitionDataService: BenchmarkFixingPrimaryLookupService,
    private notificationProcessor: NotificationEventProcessorService,
    private notificationService: NotificationService,
    private fixingSummariesService: AllBenchmarksFixingSummariesService,
    private fixingStatusService: AllBenchmarksFixingStatusService,
    private allBenchmarksRequestService: AllBenchmarksRequestService,
    private _configService: BfxConfigService,
  ) {
    super(
      base,
      factory,
      CubiqTS.OrderDataImpl,
      cubiqWorker,
      "BenchmarkOrderDataImpl",
      CubiqTS.OrderLifeCycleEventImpl,
      dataAdapter,
      notificationService,
    );

    this.broker.registerMethod(
      "getAllActiveBmarkFixings",
      [],
      this.getAllActiveBmarkFixings.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getAllRecentBmarkFixings",
      [],
      this.getAllRecentBmarkFixings.bind(this),
      PRIMITIVE,
    );
    //this.broker.registerMethod('getBmarkFixingData', [PRIMITIVE,PRIMITIVE], this.getBmarkFixingData.bind(this), PRIMITIVE);
    //this.broker.registerMethod('clearDisplayedFixing', [], this.clearDisplayedFixing.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "initBmarkUpdateService",
      [PRIMITIVE],
      this.initBmarkUpdateService.bind(this),
      null,
      false,
    );
    //
    this.broker.registerMethod(
      "getBmarkOrderDataForOrderIds",
      [PRIMITIVE],
      this.getBmarkOrderDataForOrderIds.bind(this),
      this.VALUE,
    );
    //this.broker.registerMethod('getBmarkAdaptedDataForOrderIds', [PRIMITIVE], this.getBmarkAdaptedDataForOrderIds.bind(this), PRIMITIVE);
    this.broker.registerMethod(
      "getBmarkOrderDataForFixingId",
      [PRIMITIVE, PRIMITIVE],
      this.getBmarkOrderDataForFixingId.bind(this),
      this.VALUE,
    );
    //
    this.broker.registerMethod(
      "bmarkManagerReady",
      [],
      this.bmarkManagerReady.bind(this),
      PRIMITIVE,
    );

    this.subscribeToConfiguration();

    logger.out.info("BenchmarkDataPrimaryLookupService: primeDataProfile waiting on isAuth()");
    Promise.all([
      cubiqWorker.isAuth(`${this.constructor.name}`),
      this.orderPrivatizationService.isReady(),
      this.dealPrivatizationService.isReady(),
      this.activeUserService.getActiveUserData(),
      this.sysConfigService.isPrimed(),
      this.primeRecentFixings(),
    ]).then(
      ([
        ,
        ,
        ,
        // skip
        // skip
        activeUser,
      ]) => {
        this.logBmarkInfo("BenchmarkDataPrimaryLookupService: primeDataProfile isAuth() resolved");
        this.activeUser = activeUser;
        this.base.setActiveUser(this.activeUser);

        // Order Data for Active Fixings Request
        let desc = new CubiqTS.DataProfileDescriptorImpl();
        desc.filterName = "Active Benchmarks";
        let bmarkActiveOrderDataProfileRequest = new CubiqTS.DataProfileRequestImpl();
        bmarkActiveOrderDataProfileRequest.profileDescriptors = [];
        bmarkActiveOrderDataProfileRequest.profileDescriptors.push(desc);

        let timePrivatize: number = 0,
          timeProcessFixing: number = 0,
          timeMap: number = 0,
          timeFilter: number = 0,
          ordersCount: number = 0,
          orderFiltered: number = 0,
          timeFirstOrder: number = 0;
        let timeStart: number = new Date().getTime();

        cubiqWorker
          .primeDataProfile(BLOTTERS_ACTIVE_BENCHMARKS, CubiqTS.OrderDataImpl.name)
          .subscribe(
            (orderData: CubiqTS.OrderDataImpl[]) => {
              if (
                orderData instanceof Array &&
                orderData.length > 0 &&
                orderData[0] instanceof CubiqTS.OrderDataImpl
              ) {
                let t0: number = new Date().getTime();

                if (timeFirstOrder == 0) {
                  timeFirstOrder = t0 - timeStart;
                }

                ordersCount += orderData.length;

                orderData = orderData.filter(
                  (o) =>
                    o instanceof CubiqTS.OrderDataImpl &&
                    o.orderType === CubiqTS.OrderType.BENCHMARK &&
                    o.fixingDetails.bmOrderType !== CubiqTS.BenchmarkOrderType.PositionOrder,
                );

                orderFiltered += orderData.length;

                let t1: number = new Date().getTime();

                this.processActiveBenchmarkOrderData(orderData, false);

                let t2: number = new Date().getTime();

                this.processForPrivatization(orderData);

                let t3: number = new Date().getTime();

                this.base.prime(orderData, false);

                timeFilter += t1 - t0;
                timePrivatize += t2 - t1;
                timeProcessFixing += t3 - t2;
                timeMap += new Date().getTime() - t3;

                //this.devTestDisplayOrderData(orderData);
              } else if (orderData instanceof Array && orderData.length === 0) {
                // no benchmarks -> do nothing
              } else {
                logger.out.error(
                  `ERROR: BMARK: Failed to process Active Benchmark Orders! Active Fixings =`,
                  this.base.getAllActiveFixings(),
                );
              }
            },
            (ex: Error) => {
              logger.out.error(
                `ERROR: ${this.constructor.name}: Failed to prime order data for Active fixings: `,
                ex,
              );
              this._primed.reject(ex);
            },
            () => {
              logger.out.info(
                "BMARK: Performance times: Total time filtering: " +
                  timeFilter +
                  "; Total time for fixing detection: " +
                  timeProcessFixing +
                  "; Total time for privatization " +
                  timePrivatize +
                  ". Total time for mapping " +
                  timeMap +
                  "; Filtered orders: " +
                  (ordersCount - orderFiltered) +
                  "; Time before first order: " +
                  timeFirstOrder +
                  "; Total Time: " +
                  (new Date().getTime() - timeStart),
              );
              logger.out.info(
                "BMARK: Priming benchmark data done. Proceeding with fixing updates...",
              );
              this.scheduleCheckRecentOrdersPurge();
              // Initial state update of fixings
              this.primeFixingStatus().then(() => {
                this.base.sortFixingMap();
                this.reportFixingsListSummary();
                logger.out.info(`BMARK: BenchmarkDataPrimaryLookupService set PRIMED!`);

                this.base.prime([], true);
                this.primed = true;
                this._primed.resolve(true);

                this.fireBmarkFixingEvent(
                  BmarkFixingEventOperation.UPDATE_ACTIVE_LIST,
                  this.base.getAllActiveFixings(),
                );
              });
            },
          );
      },
    );

    // Bmark Life Cycle
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.OrderLifeCycleEventImpl.name)
      .pipe(filter(($event) => this.benchmarksFilter($event as CubiqTS.OrderLifeCycleEventImpl)))
      .subscribe(($event: CubiqTS.OrderLifeCycleEventImpl) => {
        let state =
          $event != null && $event.orderData != null && $event.orderData.orderState != null
            ? CubiqTS.OrderState[$event.orderData.orderState]
            : null;
        let txTime: CubiqTS.Moment =
          $event != null && $event.orderData != null && $event.orderData.txTime != null
            ? $event.orderData.txTime
            : null;
        let timeCode = txTime.format("HH:mm:ss.SSS");

        this.logBmarkInfo(
          `CubiqTS.OrderLifeCycleEventImpl state: ${state} txTime: ${timeCode} => `,
          $event,
          true,
        );
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(
            `QUEUED Event ${
              CubiqTS.OrderLifeCycleEventImpl.name
            } fixing ${BmarkFixingData.generateFixingDataLogMsg($event.orderData)}`,
            $event,
            true,
          );
          this.orderDataEventQueue.push(
            new QueuedEvent($event, this.processBmarkOrderLifecycleEvent.bind(this)),
          );
        } else {
          this.processBmarkOrderLifecycleEvent($event);
          this.devTestDisplayOrderData([$event.orderData]);
        }
      });

    // Deal processing for deal state
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.DealImpl.name)
      .pipe(filter(($event) => this.benchmarksFilter($event as CubiqTS.DealImpl)))
      .subscribe(($event: CubiqTS.DealImpl) => {
        this.logBmarkInfo(
          `CubiqTS.DealImpl => [${
            $event instanceof CubiqTS.DealImpl
              ? $event.orderId.toString() + " - " + CubiqTS.OrderDealState[$event.dealStateId]
              : "unknown"
          }] `,
          $event,
          true,
        );
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(`QUEUED Event ${CubiqTS.DealImpl.name}`, $event, true);
          this.orderDataEventQueue.push(new QueuedEvent($event, this.processBmarkDeal.bind(this)));
        } else {
          this.processBmarkDeal($event);
        }
      });

    // Bmark Lock event
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.BenchmarkLockLifeCycleEventImpl.name)
      .pipe(
        filter(($event) =>
          this.benchmarksFilter($event as CubiqTS.BenchmarkLockLifeCycleEventImpl),
        ),
      )
      .subscribe(($event: CubiqTS.BenchmarkLockLifeCycleEventImpl) => {
        this.logBmarkInfo("CubiqTS.BenchmarkLockLifeCycleEventImpl ", $event, true);
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(
            `QUEUED Event ${CubiqTS.BenchmarkLockLifeCycleEventImpl.name}`,
            $event,
            true,
          );
          this.orderDataEventQueue.push(
            new QueuedEvent($event, this.processBmarkLockLifecycleEvent.bind(this)),
          );
        } else {
          this.processBmarkLockLifecycleEvent($event);
        }
      });
    // Bmark Xfer event
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.XferEventImpl.name)
      .pipe(filter(($event) => this.benchmarksFilter($event as CubiqTS.XferEventImpl)))
      .subscribe(($event: CubiqTS.XferEventImpl) => {
        //this.logBmarkInfo('CubiqTS.XferEventImpl ', $event, true);
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(`QUEUED Event ${CubiqTS.XferEventImpl.name}`, $event, true);
          this.orderDataEventQueue.push(
            new QueuedEvent($event, this.processBmarkXferEvent.bind(this)),
          );
        } else {
          this.processBmarkXferEvent($event);
        }
      });

    // Bulk - Bmark Life Cycle
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.BulkOrderLifeCycleEventImpl.name)
      .pipe(
        filter(($event) => this.benchmarksFilter($event as CubiqTS.BulkOrderLifeCycleEventImpl)),
      )
      .subscribe(($event: CubiqTS.BulkOrderLifeCycleEventImpl) => {
        //this.logBmarkInfo('CubiqTS.BulkOrderLifeCycleEventImpl ', $event, true);
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(
            `QUEUED Event ${CubiqTS.BulkOrderLifeCycleEventImpl.name}`,
            $event,
            true,
          );
          this.orderDataEventQueue.push(
            new QueuedEvent($event, this.processBmarkBulkOrderLifecycleEvent.bind(this)),
          );
        } else {
          this.processBmarkBulkOrderLifecycleEvent($event);
        }
      });

    // Bulk - Bmark Life Cycle
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.UnsolicitedCancelLifeCycleEventImpl.name)
      .pipe(
        filter(($event) =>
          this.benchmarksFilter($event as CubiqTS.UnsolicitedCancelLifeCycleEventImpl),
        ),
      )
      .subscribe(($event: CubiqTS.UnsolicitedCancelLifeCycleEventImpl) => {
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(
            `QUEUED Event ${CubiqTS.UnsolicitedCancelLifeCycleEventImpl.name}`,
            $event,
            true,
          );
          this.orderDataEventQueue.push(
            new QueuedEvent($event, this.processUnsolCanLifecycleEvent.bind(this)),
          );
        } else {
          this.processUnsolCanLifecycleEvent($event);
        }
      });

    // Bmark Intervention Event
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.InterventionEventImpl.name)
      .pipe(filter(($event) => this.benchmarksFilter($event as CubiqTS.InterventionEventImpl)))
      .subscribe(($event: CubiqTS.InterventionEventImpl) => {
        let txTime: CubiqTS.Moment =
          $event != null && $event.orderData != null && $event.orderData.txTime != null
            ? $event.orderData.txTime
            : null;
        let timeCode = txTime.format("HH:mm:ss.SSS");
        this.logBmarkInfo(`CubiqTS.InterventionEventImpl txTime : ${timeCode}`, $event, true);
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(`QUEUED Event ${CubiqTS.InterventionEventImpl.name}`, $event, true);
          this.orderDataEventQueue.push(
            new QueuedEvent($event, this.processBmarkInterventionEvent.bind(this)),
          );
        } else {
          this.processBmarkInterventionEvent($event);
        }

        this.interventionEventSubject.next($event);
      });

    // Bmark Order (Fixing) Update - for fill error
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.FixingOrderUpdateEventImpl.name)
      .subscribe(($event: CubiqTS.FixingOrderUpdateEventImpl) => {
        //this.logBmarkInfo('CubiqTS.FixingOrderUpdateEventImpl ', $event, true);
        if ($event != null && this.control.queueOrderDataEvents) {
          this.logBmarkInfo(
            `QUEUED Event ${CubiqTS.FixingOrderUpdateEventImpl.name}`,
            $event,
            true,
          );
          this.orderDataEventQueue.push(
            new QueuedEvent($event, this.processBmarkFixingOrderUpdate.bind(this)),
          );
        } else {
          this.processBmarkFixingOrderUpdate($event);
        }
      });

    // Fixing Event
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.FixingEventImpl.name)
      .subscribe(($event: CubiqTS.FixingEventImpl) => {
        logger.out.info(
          `${this.constructor.name} received FixingEventImpl ` +
            `fixingState: ${CubiqTS.FixingState[$event?.fixingState]} ` +
            `[this.control.queueFixingEvents:${this.control.queueFixingEvents}]`,
        );

        if ($event != null && this.control.queueFixingEvents) {
          this.fixingEventQueue.push(new QueuedEvent($event, this.processFixingUpdate.bind(this)));
        } else {
          this.processFixingUpdate($event);
        }
      });

    // Fixing Definition
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.BenchmarkFixingImpl.name)
      .subscribe(($event: CubiqTS.BenchmarkFixingImpl) => {
        if ($event != null && this.control.queueFixingEvents) {
          this.logBmarkInfo(`QUEUED Event ${CubiqTS.BenchmarkFixingImpl.name}`, $event, true);
          this.fixingEventQueue.push(
            new QueuedEvent($event, this.updateFixingDefinitionMap.bind(this)),
          );
        } else {
          this.logBmarkInfo("FIXING DEFINITION UPDATE CubiqTS.BenchmarkFixingImpl: ", $event, true);
          this.updateFixingDefinitionMap($event);
        }
      });

    // AutoHedge
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.AutoHedgingStatusEventImpl.name)
      .subscribe(($event: CubiqTS.AutoHedgingStatusEventImpl) => {
        if ($event != null && this.control.queueFixingEvents) {
          this.logBmarkInfo(
            `QUEUED Event ${CubiqTS.AutoHedgingStatusEventImpl.name}`,
            $event,
            true,
          );
          this.fixingEventQueue.push(
            new QueuedEvent($event, this.processAutoHedgeStatusEvent.bind(this)),
          );
        } else {
          //this.logBmarkInfo('FIXING DEFINITION UPDATE = CubiqTS.BenchmarkFixingImpl: ', $event, true);
          this.processAutoHedgeStatusEvent($event);
        }
      });
    this.cubiqWorker
      .getEncodableObservable(CubiqTS.ManualNettingBulkEventImpl.name)
      .subscribe((event: CubiqTS.ManualNettingBulkEventImpl) => {
        if (event != null && this.control.queueFixingEvents) {
          this.logBmarkInfo(`QUEUED Event ${CubiqTS.ManualNettingBulkEventImpl.name}`, event, true);
          this.orderDataEventQueue.push(
            new QueuedEvent(event, this.processManualNettingBulkEvent.bind(this)),
          );
        } else {
          this.processManualNettingBulkEvent(event);
        }
      });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.ManualNettingEventImpl.name)
      .subscribe((event: CubiqTS.ManualNettingEventImpl) => {
        if (event != null && this.control.queueFixingEvents) {
          this.logBmarkInfo(`QUEUED Event ${CubiqTS.ManualNettingEventImpl.name}`, event, true);
          this.orderDataEventQueue.push(
            new QueuedEvent(event, this.processManualNettingEvent.bind(this)),
          );
        } else {
          this.processManualNettingEvent(event);
        }
      });

    this.fixingModelUpdateSubscription = fixingDefinitionDataService
      .getModelUpdateSubject()
      .subscribe(this.onFixingModelUpdated.bind(this));

    this.recentFixingsSubscription = this.allBenchmarksRequestService.recentFixing$.subscribe(
      (fixingData: BmarkFixingData) => {
        if (!this.base.hasRecentFixing(fixingData.id)) {
          this.base.addRecentFixing(fixingData);
        }
      },
    );

    this.sysConfigService.getSystemConfigBoolean(SysFeatures.bmNPV).then((value) => {
      BMRiskUtils.systemUseNPV = value;
    });
  }

  processAutoHedgeStatusEvent($event: CubiqTS.AutoHedgingStatusEventImpl) {
    this.autoHedgeSubject.next($event);
  }

  ngOnDestroy() {
    if (null != this.fixingModelUpdateSubscription) {
      this.fixingModelUpdateSubscription.unsubscribe();
    }

    if (null != this.configurationSubscription) {
      this.configurationSubscription.unsubscribe();
    }

    if (null != this.recentFixingsSubscription) {
      this.recentFixingsSubscription.unsubscribe();
    }
  }

  /**********************************************************************/
  /* INITIALIZATION */
  private subscribeToConfiguration(): void {
    this.configurationSubscription = this._configService.uiConfig$.subscribe((config) => {
      this.outputLog = config.general.debug;
    });
  }

  protected onFixingModelUpdated(modelEvent: CubiqTS.ModelUpdateImpl): void {
    try {
      let fixingUpdate: CubiqTS.BenchmarkFixingImpl =
        modelEvent.payLoad as CubiqTS.BenchmarkFixingImpl;
      this.updateFixingDefinitionMap(fixingUpdate);
      this.fixingModelUpdateSubject.next(fixingUpdate);
    } catch (ex) {
      logger.out.warn("Error processing onFixingModelUpdated ", ex);
    }
  }

  private devTestDisplayOrderData(
    orderData: Array<CubiqTS.OrderDataImpl>,
    optionalText: string = "",
  ): void {
    if (this.outputLog) {
      if (
        orderData instanceof Array &&
        orderData.length > 0 &&
        orderData[0] instanceof CubiqTS.OrderDataImpl
      ) {
        for (let orderItem of orderData) {
          if (
            orderItem instanceof CubiqTS.OrderDataImpl &&
            Array.isArray(orderItem.allocationData) &&
            orderItem.allocationData.length > 0
          ) {
            let result: string = optionalText;
            if (orderItem.allocationData.length === 1) {
              result +=
                "=(ROOT)-> " + this.devTestDisplayAllocationData(orderItem.allocationData[0]);
            } else if (orderItem.allocationData.length > 1) {
              for (let allocationItem of orderItem.allocationData) {
                result += "=(ROOT)-> " + this.devTestDisplayAllocationData(allocationItem);
              }
            }
            //logger.out.info(`BMARK: Fixing[${BmarkFixingData.generateFixingDataLogMsg(orderItem)}]: Order(${orderItem.orderId})${result}`);
          } else {
            logger.out.error(`BMARK: devTestDisplay(): Invalid AllocationData`, orderItem);
          }
        }
      } else {
        logger.out.error(`BMARK: devTestDisplay(): Invalid OrderData`, orderData);
      }
    }
  }

  private devTestDisplayAllocationData(aloData: CubiqTS.AllocationDataImpl): string {
    let result: string = "";
    if (aloData instanceof CubiqTS.AllocationDataImpl) {
      let allocationString =
        `[${aloData.allocationId}-${CubiqTS.AllocationType[aloData.allocationType]}] (${
          aloData.baseCcy + aloData.nonbaseCcy
        })=> ` +
        `Trade: ${
          aloData.fillDetails instanceof CubiqTS.LastFillDetailsImpl &&
          typeof aloData.fillDetails.dealId === "string"
            ? aloData.fillDetails.dealId
            : "None"
        } | `;

      if (this.base.allocationHasMetaData(aloData.allocationId.toString())) {
        let allocationMetaData = this.base.getAllocationMetaData(aloData.allocationId.toString());
        let visibleOnItsOwn =
          allocationMetaData instanceof BmarkAllocationMetaData &&
          typeof allocationMetaData.visibleOnItsOwn === "boolean"
            ? allocationMetaData.visibleOnItsOwn
              ? "Yes"
              : "No"
            : "Unknown";
        let visibleInContext =
          allocationMetaData instanceof BmarkAllocationMetaData
            ? this.base.isLocalAllocationVisibleInContext(allocationMetaData.allocationIdString)
              ? "Yes"
              : "No"
            : "Unknown";
        allocationString += `Visible: alone/context = ${visibleOnItsOwn}/${visibleInContext}; `;
      } else {
        allocationString += `Visible: No meta-data; `;
      }

      if (
        Array.isArray(aloData.componentAllocations) &&
        aloData.componentAllocations.length > 0 &&
        aloData.componentAllocations[0] instanceof CubiqTS.AllocationDataImpl
      ) {
        if (aloData.componentAllocations.length === 1) {
          result =
            allocationString + this.devTestDisplayAllocationData(aloData.componentAllocations[0]);
        } else if (aloData.componentAllocations.length > 0) {
          result = allocationString;
          for (let allocationItem of aloData.componentAllocations) {
            result += this.devTestDisplayAllocationData(allocationItem);
          }
        }
      } else {
        result = allocationString;
      }
    } else {
      result = "Invalid allocationData";
    }
    return result;
  }

  /* ******************************************************* */
  /* BENCHMARKS EVENT FILTER */
  // Returns TRUE if event is for Benchmark, FALSE otherwise
  private benchmarksFilter($event: any): boolean {
    let result: boolean = false;
    if ($event instanceof CubiqTS.ConcreteEncodable) {
      switch ($event.encodableType) {
        case "com.bcdfx.model.event.lifecycle.OrderLifeCycleEventImpl": {
          let event: CubiqTS.OrderLifeCycleEventImpl = $event as CubiqTS.OrderLifeCycleEventImpl;
          result =
            event.orderData instanceof CubiqTS.OrderDataImpl &&
            event.orderData.orderType === CubiqTS.OrderType.BENCHMARK &&
            event.orderData.fixingDetails.bmOrderType !== CubiqTS.BenchmarkOrderType.PositionOrder;
          break;
        }
        case "com.bcdfx.model.event.lifecycle.BenchmarkLockLifeCycleEventImpl": {
          result = true; // BenchmarkLockLifeCycleEventImpl => lock event type only for benchmarks
          break;
        }
        case "com.bcdfx.model.event.lifecycle.UnsolicitedCancelLifeCycleEventImpl": {
          let unsol: CubiqTS.UnsolicitedCancelLifeCycleEventImpl =
            $event as CubiqTS.UnsolicitedCancelLifeCycleEventImpl;
          if (unsol.benchmarkFg == true) {
            result = true;
          }
          break;
        }
        case "com.bcdfx.model.event.lifecycle.XferEventImpl": {
          $event = $event as CubiqTS.XferEventImpl;
          result =
            Array.isArray($event.orderData) &&
            $event.orderData[0] instanceof CubiqTS.OrderDataImpl &&
            $event.orderData[0].orderType === CubiqTS.OrderType.BENCHMARK &&
            $event.orderData[0].fixingDetails.bmOrderType !==
              CubiqTS.BenchmarkOrderType.PositionOrder;
          break;
        }
        case "com.bcdfx.model.InterventionEventImpl": {
          $event = $event as CubiqTS.InterventionEventImpl;
          result =
            $event.orderData instanceof CubiqTS.OrderDataImpl &&
            $event.orderData.orderType === CubiqTS.OrderType.BENCHMARK &&
            $event.orderData.fixingDetails.bmOrderType !== CubiqTS.BenchmarkOrderType.PositionOrder;
          break;
        }
        case "com.bcdfx.model.event.lifecycle.BulkOrderLifeCycleEventImpl": {
          let bulkOrderEvent: CubiqTS.BulkOrderLifeCycleEventImpl =
            $event as CubiqTS.BulkOrderLifeCycleEventImpl;

          // If it's previsibility bulk event it is a benchmark bulk event
          result = bulkOrderEvent.eventTypeId === CubiqTS.FxLifeCycleEventType.BULK_PREVISIBILITY;

          if (Array.isArray(bulkOrderEvent.events)) {
            return result || this.benchmarksFilter(bulkOrderEvent.events[0]);
          }
          break;
        }
        case "com.bcdfx.model.event.lifecycle.FixingOrderUpdateEventImpl": {
          $event = $event as CubiqTS.FixingOrderUpdateEventImpl;
          result =
            $event.orderData instanceof CubiqTS.OrderDataImpl &&
            $event.orderData.orderType === CubiqTS.OrderType.BENCHMARK &&
            $event.orderData.fixingDetails.bmOrderType !== CubiqTS.BenchmarkOrderType.PositionOrder;
          break;
        }
        case CubiqTS.DealImpl.enType: {
          result =
            $event instanceof CubiqTS.DealImpl && $event.orderType === CubiqTS.OrderType.BENCHMARK;
          break;
        }
      }
    }
    //this.logBmarkInfo(`BMARK: Event FILTER => ${result ? 'BENCHMARK (PROCESS)': 'NOT BENCHMARK (IGNORE)'} for ${$event.encodableType} => $event:`, $event);
    return result;
  }

  /* ******************************************************* */
  /* BENCHMARK FIXINGS - UPDATE SUBJECT STREAM */

  public initBmarkUpdateService(port: MessagePort): void {
    this.bmarkUpdateEventPorts.push(port);
  }

  public getBmarkFixingsUpdateSubject(): Subject<any> {
    return this.bmarkFixingsUpdateSubject;
  }

  protected fireBmarkFixingEvent(operation: BmarkFixingEventOperation, payload: any): any {
    if (operation in BmarkFixingEventOperation) {
      //this.updateDisplayColor(payload); // A1 alternative fixing color update (not in use for now)
      let fixingEvent = new BmarkFixingEvent(operation, payload);
      logger.out.info(
        `DEV-FIXING: Sending BMARK-FIXING-EVENT [${
          BmarkFixingEventOperation[fixingEvent.operation]
        }] => `,
        fixingEvent,
      );
      this.logBmarkInfo(
        `Bmark-Lookup: Sending BMARK-FIXING-EVENT [${
          BmarkFixingEventOperation[fixingEvent.operation]
        }] => `,
        fixingEvent,
      );
      this.bmarkFixingsUpdateSubject.next(fixingEvent);
    } else {
      logger.out.error(
        `ERROR: BMARK: fireBmarkFixingEvent(): Invalid fixing event operation or id`,
      );
    }
  }
  /* DO NOT DELETE (for now) */
  /**
   * Currently the update of fixing tile color is performed from bmark-fixings-manager.service since it looks to be more efficient.
   * The method below can be used on 5 invocation points (commented out, marked with A(n)) in this service to update the fixing tile color
   * before it gets to bmark-fixings-manager.service. However this approach doubles the number of invocations in this setting, but it could be
   * a better solution when the code is refactored so that fixings are managed by an independent service, instead of current situation where
   * this (ClientOrdersLookup) and HedgeOrdersLookup maintain each own fixings repositories.
   */
  /* protected updateDisplayColor(payload: any): void {
        if(payload != null){
            if(payload instanceof BmarkFixingData){
                payload.updateDisplayColor();
            }else if(Array.isArray(payload) && payload.length > 0 && payload[0] instanceof BmarkFixingData){
                for(let payloadItem of payload){
                    payloadItem.updateDisplayColor();
                }
            }
        }
    } */

  /* ******************************************************* */
  /* BENCHMARK FIXINGS - PUBLIC API  */
  public bmarkManagerReady(): Promise<boolean> {
    return new Promise<any>((res, rej) => {
      this.flushQueuedFixingUpdateEvents();
      this.flushQueuedBmarkOrderDataEvents();
      res(true);
    });
  }

  public getAllActiveBmarkFixings(): Promise<Array<BmarkFixingData>> {
    return new Promise<any>((res, rej) => {
      let activeFixings = this.base.getAllActiveFixings();
      //this.updateDisplayColor(activeFixings); // A2 alternative fixing color update (not in use for now)
      res(activeFixings);
    });
  }

  public getAllRecentBmarkFixings(): Promise<Array<BmarkFixingData>> {
    return new Promise<Array<BmarkFixingData>>((res, rej) => {
      res(this.base.getAllRecentFixings());
    });
  }

  public getBmarkFixingDataAsEvent(fixingId: string, fixingType: BmarkFixingType): boolean {
    // DEV-FIXING - remove log
    logger.out.info(
      `DEV-FIXING: CLIENT-LOOKUP -> [${fixingId}] Processing request, data as EVENT!`,
    );
    if (
      typeof fixingId === "string" &&
      fixingType in BmarkFixingType &&
      this.base.hasFixingId(fixingId)
    ) {
      switch (fixingType) {
        case BmarkFixingType.ACTIVE:
          this.displayedRecentFixing = null;
          let activeFixingData = this.base.getActiveFixingData(fixingId);
          this.fireBmarkFixingEvent(BmarkFixingEventOperation.UPDATE_DATA, activeFixingData);
          return true;
          break;
        case BmarkFixingType.RECENT:
          let fixingData = this.base.getRecentFixingData(fixingId);
          if (fixingData instanceof BmarkFixingData) {
            //this.displayedRecentFixing = fixingId;
            this.retrieveOrderDataForRecentFixing(fixingId);
            return true;
          }
          break;
      }
    }
    return false;
  }

  public setDisplayedRecentFixing(fixingId: string): void {
    if (
      typeof fixingId == "string" &&
      fixingId != this.displayedRecentFixing &&
      this.base.hasRecentFixing(fixingId)
    ) {
      // displayed fixing has changed, set purge timestamp to purge orders after offset
      this.setPurgeTimestamp(this.displayedRecentFixing);
      //
      this.displayedRecentFixing = fixingId;
    }
  }

  public clearDisplayedRecentFixing(): void {
    this.setPurgeTimestamp(this.displayedRecentFixing);
    this.displayedRecentFixing = null;
  }

  private setPurgeTimestamp(fixingId: string): void {
    if (typeof fixingId === "string" && this.base.hasRecentFixing(fixingId)) {
      let fixingDataToPurgeOrders = this.base.getRecentFixingData(fixingId);
      if (fixingDataToPurgeOrders != null) {
        fixingDataToPurgeOrders.purgeTimestamp = parseInt(CubiqTS.moment().format("x"));
      }
    }
  }

  /* ******************************************************* */
  /* BENCHMARK ORDER DATA - PUBLIC API  */

  // added for context menu check
  public hasBmarkOrderDataForOrderId(id: string): boolean {
    return this.base.hasItem(id);
  }

  public getBmarkOrderDataForOrderIds(ids: Array<string>): Promise<Array<BmarkOrderData>> {
    return new Promise<Array<BmarkOrderData>>((res, rej) => {
      this.isPrimed().then(() => {
        res(this.base.getBmarkOrderDataForOrderIds(ids));
      });
    });
  }

  public getBmarkOrderDataForFixingId(
    fixingId: string,
    fixingType: BmarkFixingType,
  ): Promise<Array<BmarkOrderData>> | null {
    return new Promise<Array<BmarkOrderData>>((res, rej) => {
      //logger.out.warn(`DEV-FIXING: CLIENT-LOOKUP -> [${fixingId}] Processing request, data as PROMISE!`)
      if (typeof fixingId === "string" && fixingType in BmarkFixingType) {
        switch (fixingType) {
          case BmarkFixingType.ACTIVE: {
            let activeFixings = this.base.getBmarkOrderDataForFixingId(fixingId, fixingType);
            if (Array.isArray(activeFixings)) res(activeFixings);
            else res([]);
            break;
          }
          case BmarkFixingType.RECENT: {
            let fixingData = this.base.getRecentFixingData(fixingId);
            if (fixingData != null) {
              this.retrieveOrderDataForRecentFixing(fixingId, true).then((values) => {
                res(values != null ? values : []);
              });
            } else {
              res([]);
            }
            break;
          }
          default: {
            res([]);
            break;
          }
        }
      } else {
        res([]);
      }
    });
  }

  /* ******************************************************* */
  /* SERVER REQUEST  */
  protected primeFixingStatus(): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      let statusRequest = new CubiqTS.FixingStatusRequestImpl();

      this.fixingStatusService.primeBenchmark().then(
        (response) => {
          //this.logBmarkInfo(`BMARK: Bmark-Lookup: Retrieve Updates Response: `, response);
          if (response instanceof CubiqTS.FixingStatusResponseImpl) {
            this.fixingStatusResponseSubject.next(response);
            if (response.fixingEvent instanceof Array && response.fixingEvent.length > 0) {
              for (let fixingEvent of response.fixingEvent) {
                this.processFixingUpdate(fixingEvent);
              }
            }
            res(true);
          } else {
            logger.out.error(
              `ERROR: ${this.constructor.name}.updateFixingsStatus(): Invalid response`,
            );
            res(false);
          }
        },
        (reject) => {
          res(false);
        },
      );
    });
  }

  protected primeRecentFixings(): Promise<void> {
    return new Promise<void>((res, rej) => {
      Promise.all([
        this.sysConfigService.getSystemConfig(SysFeatures.fixingsRecentPurgeOffset),
        this.createFixingDefinitionMap(),
      ]).then(
        ([fixingsRecentPurgeOffset]) => {
          this.fixingsRecentPurgeOffset = parseInt(fixingsRecentPurgeOffset) * 3600 * 1000;
          // this.fixingDefinitionMap set in this createFixingDefinitionMap() side effect
          this.fixingSummariesService.primeBenchmark().then(
            (recentFixings) => {
              if (recentFixings != null && Array.isArray(recentFixings.results)) {
                this.processRecentBenchmarkOrderData(recentFixings.results);
                this.recentFixingSubject.next(recentFixings.results);
              }
              res();
            },
            (err?) => {
              rej(err);
            },
          );
        },
        (err?) => {
          rej(err);
        },
      );
    });
  }

  protected retrieveUpdateForFixingId(fixingId: string): void {
    if (typeof fixingId === "string" && this.base.hasFixingId(fixingId)) {
      let fixingData = this.base.getFixingData(fixingId);
      let timeCode = new CubiqTS.BMTimeCodeImpl();
      timeCode.code = fixingData.code;
      timeCode.time = CubiqTS.moment(fixingData.fixingTimestamp);
      let statusRequest = new CubiqTS.FixingStatusRequestImpl();
      statusRequest.bMTimeCode = timeCode;

      this.cubiqWorker.postServiceRequest(statusRequest).then((response: any) => {
        logger.out.warn(
          `BMARK: updateFixingStatusWithId(): Response for '${fixingId}': `,
          response,
        ); //${id}
        if (response instanceof CubiqTS.FixingStatusResponseImpl) {
          if (response.fixingEvent instanceof Array && response.fixingEvent.length > 0) {
            for (let fixingEvent of response.fixingEvent) {
              this.processFixingUpdate(fixingEvent);
            }
          }
        }
      });
    }
  }

  /*********************************************************
   *   This method performs retrieval of bmark data from AllBenchmarksRequestService (either cached or server).
   *   The method is designed for 2 purposes (based on promiseRetrieval input), first (default) it will retrieve the bmark order
   *   data and send an fixing update event  which will be consumed by the fixing manager (the returned promise is not used), and
   *   second as recent fixings data provider for the blotter which uses the data returned in the promise.
   *
   * The method also supports queueing of multiple requests for event based fixing update delivery.
   */
  protected retrieveOrderDataForRecentFixing(
    fixingId: string,
    resolveAsPromise: boolean = false,
  ): Promise<Array<BmarkOrderData>> {
    return new Promise<Array<BmarkOrderData>>((res, rej) => {
      let fixingData: BmarkFixingData = null;

      /*
             Fixing might be marked as recent via processing the fixing event on the oposing lookup while the 
             event has still not been processed by this one. In this case check for its presence in active fixings
             and see if the instance has it marked as recent. 
            */
      if (fixingId != null) {
        let recentFixingDataId = BmarkFixingData.getIdAsRecent(fixingId);
        fixingData = this.base.getRecentFixingData(recentFixingDataId);

        if (recentFixingDataId !== fixingId) {
          logger.out.warn(
            `${this.constructor.name}.retrieveOrderDataForRecentFixing() fixingId: ${fixingId} mismatch recent ${recentFixingDataId}`,
          );
        }

        if (fixingData == null) {
          let activeFixingDataId: string = BmarkFixingData.getIdAsActive(fixingId);
          if (activeFixingDataId != null) {
            let activeFixingData: BmarkFixingData =
              this.base.getActiveFixingData(activeFixingDataId);
            if (activeFixingData != null && activeFixingData.type === BmarkFixingType.RECENT) {
              fixingData = activeFixingData;
            }
          }
        }
      } else {
        logger.out.warn(
          `${this.constructor.name}.retrieveOrderDataForRecentFixing(fixingId:${fixingId}, resolveAsPromise: ${resolveAsPromise})`,
        );
      }

      if (fixingData != null) {
        let invokingFixingId = fixingId;
        fixingId = !resolveAsPromise ? this.setRetrievalRecentFixingId(fixingId) : fixingId;

        logger.out.info(
          `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> [${invokingFixingId}] = Retrieval INVOKED ${
            fixingId != invokingFixingId ? "on [" + fixingId + "]" : ""
          }` +
            ` => resolveAsPromise: ${resolveAsPromise ? "YES" : "NO"} | Retrieval status: ` +
            `${
              this.retrievalRecentFixingId === null
                ? "EMPTY"
                : this.retrievalRecentFixingId === fixingId
                  ? "IN PROGRESS"
                  : "BUSY | Queue size: " + this.retrievalRecentProcessingQueue.size
            }`,
        );

        if (fixingId != null) {
          let toBeDeletedFixing: BmarkFixingData | null = null;
          let bmarkOrderDataset = [];

          this.isPrimed().then((value) => {
            this.allBenchmarksRequestService.requestBenchmarkOrders(fixingData).subscribe(
              (orderData: AllBenchmarksResponse) => {
                logger.out.info(
                  `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> [${fixingId}] Dataset returned: size ${orderData.length}`,
                );
                if (orderData.responseLength === 0) {
                  // Send REMOVE event, no orders
                  let recentFixingData = this.base.getRecentFixingData(fixingId);
                  if (recentFixingData) {
                    toBeDeletedFixing = BmarkFixingData.retain(recentFixingData);
                  }
                } else if (Array.isArray(orderData)) {
                  orderData = orderData.map((o) =>
                    this.base.processLocalBmarkOrderDataForVisibility(BmarkOrderData.clone(o)),
                  );
                  this.base.setOrderIdsToRecentFixing(fixingId, orderData);
                  this.processForPrivatization(orderData);
                  this.base.prime(orderData);
                  orderData = orderData.map((order: CubiqTS.OrderDataImpl) =>
                    this.base.getItem(order.orderId.toString()),
                  );
                  bmarkOrderDataset = bmarkOrderDataset.concat(orderData);

                  if (orderData.length > 0) {
                    this.devTestDisplayOrderData(orderData);
                  }
                }
              },
              (ex: Error) => {
                logger.out.error(
                  `ERROR: ${this.constructor.name}: Failed to retrieve order data for Recent fixings: `,
                  ex,
                );
                this.clearRetrievalRecentFixingId();
                res(null);
              },
              () => {
                logger.out.info(
                  `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> Promise Complete [${fixingId}] | Promise Retrieval: ${
                    resolveAsPromise ? "YES" : "NO"
                  }` +
                    ` Fixing retrieval match:  ${
                      this.retrievalRecentFixingId === fixingId ? "YES" : "NO"
                    } | Retrieval request [${this.retrievalRecentFixingId}] `,
                );
                if (!resolveAsPromise || this.retrievalRecentFixingId === fixingId) {
                  this.clearRetrievalRecentFixingId();
                  if (toBeDeletedFixing == null) {
                    logger.out.info(
                      `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> Promise Complete [${fixingId}] = UPDATE `,
                    );
                    this.fireBmarkFixingEvent(BmarkFixingEventOperation.UPDATE_DATA, fixingData);
                  } else {
                    logger.out.info(
                      `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> Promise Complete [${fixingId}] = DELETING `,
                    );
                    this.fireBmarkFixingEvent(BmarkFixingEventOperation.REMOVE, toBeDeletedFixing);
                    BmarkFixingData.release(toBeDeletedFixing);
                    this.base.deleteRecentFixing(fixingId);
                    bmarkOrderDataset = [];
                  }
                }

                res(bmarkOrderDataset);

                let nextQueuedFixingId = this.getNextRetrievalFixingFromQueue();
                if (
                  nextQueuedFixingId != null &&
                  (!resolveAsPromise || this.retrievalRecentFixingId === fixingId)
                ) {
                  logger.out.info(
                    `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> [${nextQueuedFixingId}] = SET FOR RETRIEVAL ( from queue) | Queue size: ${this.retrievalRecentProcessingQueue.size}`,
                  );
                  this.retrieveOrderDataForRecentFixing(nextQueuedFixingId);
                  return;
                }
              },
            );
          });
        }
      } else {
        res([]);
      }
    });
  }

  /* RETRIEVAL QUEUE MANAGEMENT - START  */
  private setRetrievalRecentFixingId(fixingId: string): string | null {
    if (typeof fixingId === "string") {
      let now = parseInt(CubiqTS.moment().format("x"));
      if (this.retrievalRecentFixingId == null) {
        this.retrievalRecentFixingId = fixingId;
        this.retrievalRecentFixingTimestamp = now + this.retrievalRecentFixingProcessTimeOffset;
        logger.out.info(
          `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> [${fixingId}] = SET FOR RETRIEVAL (queue empty)`,
        );
        return fixingId;
      } else if (now > this.retrievalRecentFixingTimestamp) {
        // cancel current fixing, time expired
        let canceledRecentFixingId = this.retrievalRecentFixingId;
        this.clearRetrievalRecentFixingId();
        logger.out.info(
          `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> [${fixingId}] = CANCEL/INTERRUPT (resolution taking too long)`,
        );
        // process queue
        let nextQueuedFixingId = this.getNextRetrievalFixingFromQueue(); // take from queue
        if (nextQueuedFixingId != null) {
          // queue was not empty, take
          this.retrievalRecentFixingId = nextQueuedFixingId;
          this.retrievalRecentFixingTimestamp = now + this.retrievalRecentFixingProcessTimeOffset;
          logger.out.info(
            `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> [${nextQueuedFixingId}] = SET FOR RETRIEVAL (after cancel, from queue) | Queue size: ${this.retrievalRecentProcessingQueue.size}`,
          );
          if (
            fixingId != canceledRecentFixingId &&
            fixingId != nextQueuedFixingId &&
            !this.retrievalRecentProcessingQueue.has(fixingId)
          ) {
            // add new to queue if appropriate
            this.retrievalRecentProcessingQueue.add(fixingId);
            logger.out.info(
              `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> [${fixingId}] = ADD TO QUEUE`,
            );
          }
          return nextQueuedFixingId;
        } else if (fixingId != canceledRecentFixingId) {
          // nothing in queue
          this.retrievalRecentFixingId = fixingId;
          this.retrievalRecentFixingTimestamp = now + this.retrievalRecentFixingProcessTimeOffset;
          logger.out.info(
            `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> [${fixingId}] = SET FOR RETRIEVAL (after cancel, queue empty)`,
          );
          return fixingId;
        }
      } else if (this.retrievalRecentFixingId === fixingId) {
        logger.out.info(
          `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> [${fixingId}] = IGNORE (being processed atm)`,
        );
      } else if (
        fixingId != this.retrievalRecentFixingId &&
        !this.retrievalRecentProcessingQueue.has(fixingId)
      ) {
        this.retrievalRecentProcessingQueue.add(fixingId);
        logger.out.info(
          `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> [${fixingId}] = ADD TO QUEUE`,
        );
      } else {
        logger.out.info(
          `Recent Fixing Retrieval: BMARK-CLIENT-LOOKUP -> [${fixingId}] = IGNORE (already in queue)`,
        );
      }
    }
    return null;
  }

  private clearRetrievalRecentFixingId(): void {
    this.retrievalRecentFixingId = null;
    this.retrievalRecentFixingTimestamp = null;
  }

  private getNextRetrievalFixingFromQueue(): string | null {
    let nextQueuedFixingId = null;
    if (this.retrievalRecentProcessingQueue.size > 0) {
      nextQueuedFixingId = this.retrievalRecentProcessingQueue.values().next().value;
      this.retrievalRecentProcessingQueue.delete(nextQueuedFixingId);
    }
    return nextQueuedFixingId;
  }

  /* RETRIEVAL QUEUE MANAGEMENT - END  */

  /* ******************************************************* */
  /* FIXING PROCESSING - CREATION */

  /* RECENT */
  public processRecentBenchmarkOrderData(recentBmarks: Array<CubiqTS.BMTimeCodeImpl>): void {
    if (
      recentBmarks instanceof Array &&
      recentBmarks.length > 0 &&
      recentBmarks[0] instanceof CubiqTS.BMTimeCodeImpl
    ) {
      for (let recentBmark of recentBmarks) {
        let time: CubiqTS.Moment = recentBmark.time as CubiqTS.moment.Moment;
        logger.out.info(
          `${this.constructor.name}.processRecentBenchmarkOrderData() Recent Benchmark ${
            recentBmark.code
          } ${time.format("DD-MMM-YYYY HH:mm")}`,
        );
        this.createRecentFixingsFromBMTimeCode(recentBmark);
      }
      this.fireBmarkFixingEvent(
        BmarkFixingEventOperation.UPDATE_RECENT_LIST,
        this.base.getAllRecentFixings(),
      );
    }
  }

  public getSpawnedOrderCount(clientOrderIds: Array<string>): number {
    let orders: Array<BmarkOrderData> = this.base.getBmarkOrderDataForOrderIds(clientOrderIds);
    let count: number = 0;

    if (orders != null && orders.length > 0) {
      orders
        .filter((order: BmarkOrderData) => this.activeUser.canSeeBmarkOrder(order))
        .forEach((order: BmarkOrderData) => {
          order.allocationData.forEach((allocation: CubiqTS.AllocationDataImpl) => {
            if (allocation.allocationType == CubiqTS.AllocationType.PRIME) {
              count++;
            } else if (allocation.allocationType == CubiqTS.AllocationType.CROSS) {
              count += allocation.componentAllocations.length;
            }
          });
        });
    }

    return count;
  }

  private createRecentFixingsFromBMTimeCode(recentBmark: CubiqTS.BMTimeCodeImpl): void {
    // try{
    if (
      recentBmark instanceof CubiqTS.BMTimeCodeImpl &&
      typeof recentBmark.code === "string" &&
      recentBmark.time != null //instanceof CubiqTS.moment.moment
    ) {
      let newFixing = BmarkFixingData.create(
        recentBmark.code,
        recentBmark.time,
        BmarkFixingType.RECENT,
        this.getFixingDefinitionCopy(recentBmark.code),
      );
      let newFixingId = newFixing.id;

      if (!this.shouldBePurgedOffset(recentBmark.time.format("x"))) {
        if (!this.base.hasRecentFixing(newFixingId)) {
          this.base.addRecentFixing(newFixing);
          this.fireBmarkFixingEvent(BmarkFixingEventOperation.ADD, newFixing);
          this.allBenchmarksRequestService.addRecentFixing(newFixing);
        } else {
          logger.out.error(
            `ERROR: BMARK: createRecentFixingsFromBMTimeCode(): Recent fixing [${newFixingId}] already exists`,
          );
        }
      } else {
        this.logBmarkInfo(
          `Bmark-Lookup: Recent Fixing [${newFixingId}] ignored on creation - too old`,
        );
      }
    } else {
      logger.out.error(`ERROR: BMARK: createRecentFixingsFromBMTimeCode(): Invalid recent fixing`);
    }
    // }catch(ex){
    //     logger.out.error(ex);
    // }
  }

  /* ACTIVE */
  public processActiveBenchmarkOrderData(
    orderDataSet: Array<CubiqTS.OrderDataImpl>,
    fireUpdateFixingEvent: boolean = true,
  ): void {
    if (
      orderDataSet instanceof Array &&
      orderDataSet.length > 0 &&
      orderDataSet[0] instanceof CubiqTS.OrderDataImpl
    ) {
      for (let orderData of orderDataSet) {
        this.createFixingsFromOrderData(orderData);
      }

      if (fireUpdateFixingEvent) {
        this.fireBmarkFixingEvent(
          BmarkFixingEventOperation.UPDATE_ACTIVE_LIST,
          this.base.getAllActiveFixings(),
        );
      }
    }
  }

  private createFixingsFromOrderData(orderData: CubiqTS.OrderDataImpl): string | null {
    let retFixingId: string = null;

    if (!this.hasBmarkFixingData(orderData)) {
      return null;
    }

    const fixingTimestamp = parseInt(orderData.fixingDate.format("x"));
    const shouldBePurged: boolean = this.shouldBePurgedOffset(fixingTimestamp);
    const isActive =
      this.isOrderDataActive(fixingTimestamp, orderData.fixingSourceCd) && !shouldBePurged;
    const orderId = orderData.orderId.toString();

    let activeFixingId = BmarkFixingData.id(
      orderData.fixingSourceCd,
      orderData.fixingDate,
      BmarkFixingType.ACTIVE,
    );

    let recentFixingId = BmarkFixingData.id(
      orderData.fixingSourceCd,
      orderData.fixingDate,
      BmarkFixingType.RECENT,
    );

    let hasActiveFixing = this.base.hasActiveFixing(activeFixingId);
    let hasRecentFixing = this.base.hasRecentFixing(recentFixingId);

    let operation: BmarkFixingEventOperation = null;
    let fixingData: BmarkFixingData = null;
    let fixingDef = this.getFixingDefinitionCopy(orderData.fixingSourceCd);

    this.allBenchmarksRequestService.invalidateById(activeFixingId);
    this.allBenchmarksRequestService.invalidateById(recentFixingId);

    if (shouldBePurged) {
      return null;
    }

    if (isActive) {
      if (
        hasActiveFixing &&
        !this.base.hasOrderIdInActiveFixing(activeFixingId, orderData.orderId.toString())
      ) {
        this.base.addOrderToActiveFixing(activeFixingId, orderData.orderId.toString(), orderData);
        operation = BmarkFixingEventOperation.UPDATE_DATA;
      } else if (hasActiveFixing) {
        this.base.updateOrderInActiveFixing(
          activeFixingId,
          orderData.orderId.toString(),
          orderData,
        );
        operation = BmarkFixingEventOperation.UPDATE_DATA;
      } else {
        this.base.addActiveFixing(
          BmarkFixingData.create(
            orderData.fixingSourceCd,
            orderData.fixingDate,
            BmarkFixingType.ACTIVE,
            fixingDef,
          ),
        );
        this.base.addOrderToActiveFixing(activeFixingId, orderData.orderId.toString(), orderData);
        operation = BmarkFixingEventOperation.ADD;
        retFixingId = activeFixingId;
      }

      fixingData = this.base.getActiveFixingData(activeFixingId);
    } else {
      if (hasActiveFixing) {
        if (!this.base.hasOrderIdInActiveFixing(activeFixingId, orderId)) {
          this.base.addOrderToActiveFixing(activeFixingId, orderId, orderData);
        }

        this.base.moveFixingTo(activeFixingId, BmarkFixingType.RECENT);
        operation = BmarkFixingEventOperation.MOVE_TO_RECENT;
      } else if (hasRecentFixing) {
        if (!this.base.hasOrderIdInRecentFixing(recentFixingId, orderData.orderId.toString())) {
          this.base.addOrderToRecentFixing(recentFixingId, orderData.orderId.toString(), orderData);
        } else {
          this.base.updateOrderInRecentFixing(
            recentFixingId,
            orderData.orderId.toString(),
            orderData,
          );
        }
        operation = BmarkFixingEventOperation.UPDATE_DATA;
      } else {
        const fixingData = BmarkFixingData.create(
          orderData.fixingSourceCd,
          orderData.fixingDate,
          BmarkFixingType.RECENT,
          fixingDef,
        );
        this.base.addRecentFixing(fixingData);
        this.base.addOrderToRecentFixing(recentFixingId, orderData.orderId.toString(), orderData);
        this.allBenchmarksRequestService.addRecentFixing(fixingData);
        operation = BmarkFixingEventOperation.ADD;
      }
      fixingData = this.base.getRecentFixingData(recentFixingId);
    }

    if (operation != null && fixingData != null) {
      logger.out.info(
        `${this.constructor.name}.createFixingsFromOrderData() ` +
          `BmarkFixingEventOperation.${BmarkFixingEventOperation[operation]} ${fixingData.id} clientOrdersCount:${fixingData.clientOrdersCount} hedgeOrdersCount:${fixingData.hedgeOrdersCount}`,
      );
      this.fireBmarkFixingEvent(operation, fixingData);
    } else {
      logger.out.info(`${this.constructor.name}.createFixingsFromOrderData() no-op`);
    }

    return retFixingId;
  }

  private hasBmarkFixingData(orderData: CubiqTS.OrderDataImpl) {
    return (
      orderData instanceof CubiqTS.OrderDataImpl &&
      typeof orderData.fixingSourceCd === "string" &&
      orderData.fixingDate != null && //instanceof CubiqTS.moment.moment
      orderData.orderId instanceof CubiqTS.Long &&
      orderData.fixingDetails.bmOrderType !== CubiqTS.BenchmarkOrderType.PositionOrder
    );
  }

  /* ******************************************************* */
  /* FIXING PROCESSING - UPDATE */

  private processFixingUpdate(fixingEvent: CubiqTS.FixingEventImpl): void {
    if (fixingEvent instanceof CubiqTS.FixingEventImpl) {
      if (fixingEvent.bMTimeCodes instanceof Array && fixingEvent.bMTimeCodes.length > 0) {
        // just a check, can be removed
        for (let bMTimeCode of fixingEvent.bMTimeCodes) {
          if (bMTimeCode instanceof CubiqTS.BMTimeCodeImpl && bMTimeCode.time != null) {
            let activeFixingId = BmarkFixingData.generateFixingIdFromCodeMoment(
              bMTimeCode.code,
              bMTimeCode.time,
              BmarkFixingType.ACTIVE,
            );
            let recentFixingId = BmarkFixingData.generateFixingIdFromCodeMoment(
              bMTimeCode.code,
              bMTimeCode.time,
              BmarkFixingType.RECENT,
            );
            if (!this.shouldBePurgedOffset(bMTimeCode.time.format("x"))) {
              let fixingDataActive = this.base.getFixingData(activeFixingId);
              let fixingDataRecent = this.base.getFixingData(recentFixingId);

              if (fixingDataActive != null && fixingEvent.lastRevalTime != null) {
                fixingDataActive.lastRevalTime = fixingEvent.lastRevalTime;
              }

              if (fixingDataActive instanceof BmarkFixingData) {
                // Update state
                fixingDataActive.state = fixingEvent.fixingState;
                this.logBmarkInfo(
                  `Bmark-Lookup: UPDATE => Fixing [${activeFixingId}] state updated (${
                    CubiqTS.FixingState[fixingDataActive.state]
                  }) => (${CubiqTS.FixingState[fixingEvent.fixingState]})`,
                );

                if (this.base.hasRecentFixing(recentFixingId)) {
                  logger.out.error(
                    `${this.constructor.name}.processFixingUpdate() fixing in both ACTIVE and RECENT, with a move to RECENT indicated so deleting from Active`,
                  );
                  this.base.deleteActiveFixing(activeFixingId);
                } else if (
                  this.base.hasRecentFixing(recentFixingId) === false &&
                  fixingDataActive.state === CubiqTS.FixingState.COMPLETED
                ) {
                  this.base.moveFixingTo(activeFixingId, BmarkFixingType.RECENT);
                  this.logBmarkInfo(
                    `Bmark-Lookup: UPDATE => Moving [${activeFixingId}] to RECENT based on fixing data state: CubiqTS.FixingState.COMPLETED`,
                  );
                  this.fireBmarkFixingEvent(
                    BmarkFixingEventOperation.MOVE_TO_RECENT,
                    fixingDataActive,
                  );
                } else {
                  /*   if(fixingEvent.expiringOrderIds != null && fixingEvent.expiringOrderIds.length > 0) {
                                        this.fireBmarkFixingEvent(BmarkFixingEventOperation.REFRESH, fixingDataActive);
                                    }else{ */
                  this.fireBmarkFixingEvent(
                    BmarkFixingEventOperation.UPDATE_DATA,
                    fixingDataActive,
                  );
                  //}
                }

                this.dispatchBmarkFixingUpdateNotification(
                  fixingDataActive,
                  fixingEvent,
                  BmarkFixingType.ACTIVE,
                );

                if (
                  fixingEvent.fixingState === CubiqTS.FixingState.COMPLETED &&
                  Array.isArray(fixingEvent.expiringOrderIds) &&
                  fixingEvent.expiringOrderIds.length > 0
                ) {
                  this.processFixingCompleteEventForExpiredOrders(fixingEvent);
                }
              } else if (
                fixingDataRecent instanceof BmarkFixingData &&
                fixingEvent.fixingState === CubiqTS.FixingState.TRIGGERED
              ) {
                this.dispatchBmarkFixingUpdateNotification(
                  fixingDataRecent,
                  fixingEvent,
                  BmarkFixingType.RECENT,
                ); // OMS-32629
              } else {
                // not an error, just a warning some update events should be ignored
                //this.logBmarkInfo(`WARN: BMARK: ${this.constructor.name}.processFixingUpdate(): BmarkFixingData for '${activeFixingId}' not found`);
              }
            } else if (this.base.hasFixingId(activeFixingId)) {
              if (this.checkPurgeFixingData(this.base.getFixingData(activeFixingId))) {
              }
            } else if (this.base.hasFixingId(recentFixingId)) {
              if (this.checkPurgeFixingData(this.base.getFixingData(recentFixingId))) {
              }
            } else {
              //this.logBmarkInfo(`${this.constructor.name}.processFixingUpdate(): Ignoring update for [${activeFixingId}], because out of date`);
            }
          } else {
            logger.out.error(
              `ERROR: BMARK: ${this.constructor.name}.processFixingUpdate(): Invalid BMTimeCodeImpl`,
            );
          }
        }
      }
    } else {
      logger.out.error(
        `ERROR: BMARK: ${this.constructor.name}.processFixingUpdate(): Invalid FixingEventImpl`,
      );
    }
  }

  // existingFixingId - added to handle OMS-30944 (fixing time changed on modify)
  protected processOrderDataUpdateForFixings(
    orderData: CubiqTS.OrderDataImpl,
    deleteOpr: boolean = false,
    existingActiveFixingId: string = null,
  ): void {
    if (
      orderData instanceof CubiqTS.OrderDataImpl &&
      typeof orderData.fixingSourceCd === "string" &&
      orderData.fixingDate != null && //instanceof CubiqTS.moment.moment
      orderData.orderId instanceof CubiqTS.Long
    ) {
      let activeFixingId =
        typeof existingActiveFixingId === "string"
          ? existingActiveFixingId
          : BmarkFixingData.generateFixingIdFromOrderData(orderData, BmarkFixingType.ACTIVE);
      let recentFixingId: string;
      if (this.base.hasActiveFixing(activeFixingId)) {
        this.allBenchmarksRequestService.invalidateById(activeFixingId);
        if (deleteOpr) {
          let toBeDeletedFixing = this.base.getActiveFixingData(activeFixingId);
          if (toBeDeletedFixing instanceof BmarkFixingData) {
            this.base.deleteOrderIdInActiveFixing(activeFixingId, orderData.orderId.toString());
            if (toBeDeletedFixing.hasOrders) {
              // Send UPDATE event
              this.fireBmarkFixingEvent(
                BmarkFixingEventOperation.UPDATE_DATA,
                this.base.getActiveFixingData(activeFixingId),
              );
            } else {
              this.base.deleteActiveFixing(activeFixingId);
              // Send REMOVE event
              this.fireBmarkFixingEvent(BmarkFixingEventOperation.REMOVE, toBeDeletedFixing);
            }
          }
        } else {
          // ADD Order id
          if (!this.base.hasOrderIdInActiveFixing(activeFixingId, orderData.orderId.toString())) {
            // Add Order id
            this.base.addOrderToActiveFixing(
              activeFixingId,
              orderData.orderId.toString(),
              orderData,
            );

            this.fireBmarkFixingEvent(
              BmarkFixingEventOperation.UPDATE_DATA,
              this.base.getActiveFixingData(activeFixingId),
            );
          } else {
            // support Cancel Pending to Active after reject in soft-cut off OMS-30905 -dc
            this.createFixingsFromOrderData(orderData);
          }
        }
        return;
      }
      recentFixingId = BmarkFixingData.generateFixingIdFromOrderData(
        orderData,
        BmarkFixingType.RECENT,
      );
      if (this.base.hasRecentFixing(recentFixingId)) {
        let canSeeBmarkOrder = this.activeUser.canSeeBmarkOrder(orderData);
        let isInRejectedList = BENCHMARK_REJECT_STATES.has(orderData.orderState);

        this.allBenchmarksRequestService.invalidateById(recentFixingId);

        if (!canSeeBmarkOrder || isInRejectedList) {
          let toBeDeletedRecentFixing = this.base.getRecentFixingData(recentFixingId);
          if (toBeDeletedRecentFixing instanceof BmarkFixingData) {
            let fixingData = this.base.getRecentFixingData(recentFixingId);
            this.base.deleteOrderIdInRecentFixing(recentFixingId, orderData.orderId.toString());

            if (fixingData.hasOrders) {
              // Send UPDATE event
              this.fireBmarkFixingEvent(
                BmarkFixingEventOperation.UPDATE_DATA,
                this.base.getRecentFixingData(recentFixingId),
              );
            } else {
              this.base.deleteRecentFixing(recentFixingId);
              // Send REMOVE event
              this.fireBmarkFixingEvent(BmarkFixingEventOperation.REMOVE, toBeDeletedRecentFixing);
            }
          }
        } else {
          this.base.addOrderToRecentFixing(recentFixingId, orderData.orderId.toString(), orderData);
          this.fireBmarkFixingEvent(
            BmarkFixingEventOperation.UPDATE_DATA,
            this.base.getRecentFixingData(recentFixingId),
          );
        }
        return;
      }

      if (!deleteOpr) {
        // create fixing for unseen orderData
        let activeFixingId = this.createFixingsFromOrderData(orderData); // handles updates on its own
        this.allBenchmarksRequestService.invalidateById(activeFixingId);
        if (activeFixingId != null) {
          this.retrieveUpdateForFixingId(activeFixingId);
        }
      } else {
        // HedgeOrderDataPrimaryLookup was restructured and doesn't warn for this, consider dropping -dc
        logger.out.warn(
          `ERROR: BMARK: ${this.constructor.name}.updateFixingsStatus(): delete invalid or already processed for activeFixingId:${activeFixingId} existingActiveFixingId:${existingActiveFixingId} recentFixingId:${recentFixingId} not present in base. %O`,
          orderData,
        );
      }
    } else {
      logger.out.error(
        `ERROR: BMARK: ${this.constructor.name}.processOrderDataUpdateForFixings(): Invalid orderData: `,
        orderData,
      );
    }
  }

  /* ******************************************************* */
  /* FIXING DEFINITION MAP (BenchmarkFixingImpl)*/
  protected createFixingDefinitionMap(): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      try {
        // reset map
        this.fixingDefinitionMap = new Map<string, BmarkFixingDefinition>();

        this.fixingDefinitionDataService.isPrimed().then(() => {
          let fixingDefinitionsImpls = Array.from(
            this.fixingDefinitionDataService.getBase().getAll().values(),
          );
          for (let fixingDefinitionImpl of fixingDefinitionsImpls) {
            if (fixingDefinitionImpl instanceof CubiqTS.BenchmarkFixingImpl) {
              let fixingDefinition = new BmarkFixingDefinition(fixingDefinitionImpl);
              this.fixingDefinitionMap.set(fixingDefinition.code, fixingDefinition);
            }
          }

          res(true);
        });
      } catch (ex) {
        logger.out.error(`ERROR: BMARK: Failed to create fixing definition map:`, ex);
        rej(ex);
      }
    });
  }

  protected updateFixingDefinitionMap(fixingDefinitionImpl: CubiqTS.BenchmarkFixingImpl): void {
    try {
      if (fixingDefinitionImpl instanceof CubiqTS.BenchmarkFixingImpl) {
        let fixingDefinition = new BmarkFixingDefinition(fixingDefinitionImpl);
        this.fixingDefinitionMap.set(fixingDefinition.code, fixingDefinition);
        this.logBmarkInfo(`CubiqTS.BenchmarkFixingImpl updated in map`, this.fixingDefinitionMap);
      }
    } catch (ex) {
      logger.out.error(`ERROR: BMARK: Failed to update fixing definition item:`, ex);
    }
  }

  public getFixingDefinitionCopy(code: string): BmarkFixingDefinition {
    return this.fixingDefinitionMap.has(code)
      ? Object.assign(new BmarkFixingDefinition(), this.fixingDefinitionMap.get(code))
      : null;
  }

  public getSpotAlertThresholds(fixingId: string): Map<string, CubiqTS.BigNumber> {
    return this.getThresholds(
      fixingId,
      (fixingDefinition: BmarkFixingDefinition) => fixingDefinition.spotAlertThresholds,
      false,
    );
  }

  public getSpotWarningThresholds(fixingId: string): Map<string, CubiqTS.BigNumber> {
    return this.getThresholds(
      fixingId,
      (fixingDefinition: BmarkFixingDefinition) => fixingDefinition.spotWarnThresholds,
      false,
    );
  }

  public getFwdAlertThresholds(fixingId: string): Map<string, CubiqTS.BigNumber> {
    return this.getThresholds(
      fixingId,
      (fixingDefinition: BmarkFixingDefinition) => fixingDefinition.fwdAlertThresholds,
      true,
    );
  }

  public getFwdWarningThresholds(fixingId: string): Map<string, CubiqTS.BigNumber> {
    return this.getThresholds(
      fixingId,
      (fixingDefinition: BmarkFixingDefinition) => fixingDefinition.fwdWarnThresholds,
      true,
    );
  }

  /**
   * Get the thresholds from the fixing definition using the supplied mapper function. For
   * spot, we check to see that the fixing is active; for forward risk, we allow for both the
   * active and recent
   * @param fixingId
   * @param thresholdMapper
   * @param isFwd
   * @returns
   */
  private getThresholds(
    fixingId: string,
    thresholdMapper: (BmarkFixingDefinition) => Map<string, CubiqTS.BigNumber>,
    isFwd: boolean,
  ): Map<string, CubiqTS.BigNumber> {
    let ret: Map<string, CubiqTS.BigNumber> = null;
    let found: BmarkFixingData = this.base.getActiveFixingData(fixingId);

    if (!found && isFwd) {
      // For Fwd Risk blotter we show threshold indicators on both Active and recent fixings
      found = this.base.getRecentFixingData(fixingId);
    }

    if (found != null) {
      let fixingDefinition: BmarkFixingDefinition = this.fixingDefinitionMap.get(found.code);
      if (fixingDefinition != null) {
        ret = thresholdMapper(fixingDefinition);
      }
    }

    return ret;
  }

  /*
   * FIXING PURGE PROCESSING - 3 TYPES of PURGE:
   * checkPurgeOrderDataOnFixingRemoved() -> Deletes orderData for recent fixings orders that are not displayed
   * checkAllPurgeRecentFixings() -> Deletes Recent fixings older than X hours => no longer in view recent fixings list
   * checkAllSweepActiveToRecent() -> Moves Active Fixing to Recent List after sweep time passed.
   */
  protected scheduleCheckRecentOrdersPurge(): void {
    setInterval(() => {
      //logger.out.info(`BMARK: Bmark-Lookup: THE PURGE TAKING PLACE NOW!`)
      this.checkPurgeOrderDataAfterRemoval();
      // this.checkAllSweepActiveToRecent();
    }, this.fixingsRecentPurgeInterval);
  }

  protected checkPurgeOrderDataAfterRemoval(): void {
    let recentFixings = this.base.getAllRecentFixings();

    if (recentFixings instanceof Array && recentFixings.length > 0) {
      for (let recentFixing of recentFixings) {
        if (
          !this.checkPurgeFixingData(recentFixing) &&
          recentFixing.hasClientOrders() &&
          this.shouldBePurgedIdle(recentFixing.id, recentFixing.purgeTimestamp)
        ) {
          this.base.clearOrderIdsInRecentFixing(recentFixing.id);
          for (let orderId of recentFixing.getClientOrderIds()) {
            this.base.deleteItemById(orderId);
            this.fireModelDeleteEvent(orderId);
          }
          this.logBmarkInfo(`Bmark-Lookup: Purging IDLE Fixing [${recentFixing.id}] orders`);
        }
      }
    }
  }

  private shouldBePurgedIdle(fixingId: string, purgeTimestamp: number | string): boolean {
    try {
      purgeTimestamp =
        typeof purgeTimestamp === "string" ? parseInt(purgeTimestamp) : purgeTimestamp;
      let currentTimestamp = parseInt(CubiqTS.moment().format("x"));
      return (
        fixingId !== this.displayedRecentFixing &&
        currentTimestamp >= purgeTimestamp + this.fixingsRecentOrdersIdleOffset
      );
    } catch (ex) {
      logger.out.error(`${this.constructor.name}.shouldBePurgedIdle() execption`, ex);
      return false;
    }
  }

  protected checkAllPurgeRecentFixings(): void {
    let recentFixings = this.base.getAllRecentFixings();
    if (recentFixings instanceof Array && recentFixings.length > 0) {
      for (let recentFixing of recentFixings) {
        this.checkPurgeFixingData(recentFixing);
      }
    }
  }

  protected checkPurgeFixingData(fixingData: BmarkFixingData): boolean {
    if (fixingData instanceof BmarkFixingData) {
      let fixingId = fixingData.id;
      let fixingType = fixingData.type;
      if (this.shouldBePurgedOffset(fixingData.fixingTimestamp)) {
        fixingType === BmarkFixingType.RECENT
          ? this.base.deleteRecentFixing(fixingId)
          : this.base.deleteActiveFixing(fixingId);
        this.logBmarkInfo(
          `Bmark-Lookup: Fixing [${fixingId}] of ${BmarkFixingType[fixingType]} PURGED - too old to live!`,
        );
        this.fireBmarkFixingEvent(BmarkFixingEventOperation.REMOVE, fixingData);

        return true;
      }
    }
    return false;
  }

  private shouldBePurgedOffset(fixingTimestamp: number | string): boolean {
    try {
      fixingTimestamp =
        typeof fixingTimestamp === "string" ? parseInt(fixingTimestamp) : fixingTimestamp;
      let currentTimestamp = parseInt(CubiqTS.moment().format("x"));
      return currentTimestamp >= fixingTimestamp + this.fixingsRecentPurgeOffset;
    } catch (ex) {
      return false;
    }
  }

  /* THE SWEEPING MOTION */
  protected checkAllSweepActiveToRecent(): void {
    let activeFixings = this.base.getAllActiveFixings();
    if (activeFixings instanceof Array && activeFixings.length > 0) {
      for (let activeFixing of activeFixings) {
        this.sweepFixingData(activeFixing);
      }
    }
  }

  protected sweepFixingData(fixingData: BmarkFixingData): boolean {
    let sweepTimeOffset: number = this.defaultSweepOffset;

    if (fixingData != null && fixingData.fixingDef != null) {
      sweepTimeOffset = fixingData.fixingDef.sweepTimeOffset;
    } else if (fixingData != null) {
      let fixingDefinition: BmarkFixingDefinition = this.fixingDefinitionMap.get(fixingData.code);
      if (fixingDefinition != null) {
        fixingData.fixingDef = fixingDefinition;
        sweepTimeOffset = fixingData.fixingDef.sweepTimeOffset;
      }
    }

    if (fixingData instanceof BmarkFixingData) {
      if (this.isOrderDataActive(fixingData.fixingTimestamp, fixingData.code)) {
        this.base.moveFixingTo(fixingData.id, BmarkFixingType.RECENT);
        this.logBmarkInfo(
          `Bmark-Lookup: SWEEP => Moving [${fixingData.id}] to RECENT based on shouldFixingDataBeSwept() returning true`,
        );
        this.fireBmarkFixingEvent(BmarkFixingEventOperation.MOVE_TO_RECENT, fixingData);
        return true;
      }
    }

    return false;
  }

  protected isOrderDataActive(fixingTimestamp: number, code: string): boolean {
    let sweepTimeOffset: number = this.defaultSweepOffset;
    let currentTimestamp = parseInt(CubiqTS.moment().format("x"));
    let isActive: boolean = false;
    let fixingDefinition: BmarkFixingDefinition = this.fixingDefinitionMap.get(code);

    if (fixingDefinition != null) {
      sweepTimeOffset = fixingDefinition.sweepTimeOffset;

      if (typeof sweepTimeOffset === "string") sweepTimeOffset = parseInt(sweepTimeOffset);

      if (typeof sweepTimeOffset !== "number" || isNaN(sweepTimeOffset))
        sweepTimeOffset = this.defaultSweepOffset;
    }

    isActive = currentTimestamp < fixingTimestamp + sweepTimeOffset;

    return isActive;
  }

  /* ******************************************************* */
  /* BENCHMARK FIXINGS - REPORTING */

  private reportFixingsListSummary(): void {
    let recentFixings = this.base.getAllRecentFixings();
    let recentCount = 0;
    for (let recentFixing of recentFixings) {
      recentCount = recentCount + recentFixing.clientOrdersCount;
    }
    this.logBmarkInfo(
      `Bmark-Lookup: RECENT Fixings: ${recentFixings
        .map((item) => item.id)
        .toString()} | total orders: ${recentCount}`,
    );

    // Active fixings report - to be deleted
    let activeFixings = this.base.getAllActiveFixings();
    let activeCountOrders = 0;
    let activeCountHCO = 0;
    let activeCountSCO = 0;
    for (let activeFixing of activeFixings) {
      activeCountOrders = activeCountOrders + activeFixing.clientOrdersCount;
      activeCountHCO =
        activeFixing.state === CubiqTS.FixingState.HARD_CUTOFF
          ? activeCountHCO + 1
          : activeCountHCO;
      activeCountSCO =
        activeFixing.state === CubiqTS.FixingState.SOFT_CUTOFF
          ? activeCountSCO + 1
          : activeCountSCO;
    }
    this.logBmarkInfo(
      `Bmark-Lookup: ACTIVE Fixings: ${activeFixings.map((item) => item.id).toString()} | ` +
        `HCO (${activeCountHCO}) => [${
          activeCountHCO > 0
            ? activeFixings
                .filter((item) => item.state === CubiqTS.FixingState.HARD_CUTOFF)
                .map((item) => item.id)
            : "N/A"
        }] | ` +
        `SCO (${activeCountSCO}) => [${
          activeCountSCO > 0
            ? activeFixings
                .filter((item) => item.state === CubiqTS.FixingState.SOFT_CUTOFF)
                .map((item) => item.id)
            : "N/A"
        }] | ` +
        `total orders: ${activeCountOrders}`,
    );

    let duplicates = this.checkDuplicateIds();
    if (duplicates.length > 0) {
      this.logBmarkInfo(`Bmark-Lookup: DUPLICATE FIXINGS: `, duplicates);
    }
  }

  private checkDuplicateIds(): Array<string> {
    let activeFixingsIds = this.base.getAllActiveFixingsIds();
    let recentFixingsIds = this.base.getAllRecentFixingsIds();
    let duplicates = new Array<string>();
    if (recentFixingsIds instanceof Array && recentFixingsIds.length > 0) {
      for (let recentFixingId of recentFixingsIds) {
        let result = activeFixingsIds.find((item) => item === recentFixingId);
        if (result != null) {
          duplicates.push(recentFixingId);
        }
      }
    }
    return duplicates;
  }

  /* ******************************************************* */

  @ModelUpdate(CubiqTS.ClientImpl)
  protected onClientUpdate(client: CubiqTS.ClientImpl, order: CubiqTS.OrderDataImpl): boolean {
    return order.clientCode == client.clientId;
  }

  @ModelUpdate(CubiqTS.AccountImpl)
  protected onAccountUpdate(account: CubiqTS.AccountImpl, order: CubiqTS.OrderDataImpl): boolean {
    return order.accountCode == account.id;
  }

  @ModelUpdate(CubiqTS.CurrencyImpl)
  protected onCurrencyUpdate(
    currency: CubiqTS.CurrencyImpl,
    order: CubiqTS.OrderDataImpl,
  ): boolean {
    return order.baseCode == currency.id || order.nonBaseCode == currency.id;
  }

  @ModelUpdate(CubiqTS.CurrencyPairImpl)
  protected onCurrencyPairUpdate(
    currencyPair: CubiqTS.CurrencyPairImpl,
    order: CubiqTS.OrderDataImpl,
  ): boolean {
    return order.baseCode == currencyPair.base && order.nonBaseCode == currencyPair.nonBase;
  }

  @ModelUpdate(CubiqTS.TradingGroupImpl)
  protected onTraderGroupUpdate(
    traderGroup: CubiqTS.TradingGroupImpl,
    order: CubiqTS.OrderDataImpl,
  ): boolean {
    return (
      this.compareIds(order.deskId, traderGroup.groupId) ||
      this.compareIds(order.targetDeskId, traderGroup.groupId)
    );
  }

  @ModelUpdate(CubiqTS.SalesGroupImpl)
  protected onSalesGroupUpdate(
    salesGroup: CubiqTS.SalesGroupImpl,
    order: CubiqTS.OrderDataImpl,
  ): boolean {
    return this.compareIds(order.salesGroupId, salesGroup.groupId);
  }

  @ModelUpdate(CubiqTS.UserImpl)
  protected onUserUpdate(user: CubiqTS.UserImpl, order: CubiqTS.OrderDataImpl): boolean {
    return (
      this.compareIds(order.entryUserId, user.userId) ||
      this.compareIds(order.lockUserId, user.userId) ||
      this.compareIds(order.transferUserId, user.userId)
    );
  }

  /* ******************************************************* */
  /* BMARK ORDER-DATA UPDATE PRIMARY SUBJECT */

  public getBmarkOrderDataUpdateSubject(): Subject<BmarkOrderDataEvent> {
    return this.bmarkOrderDataUpdateSubject;
  }

  public getInterventionEventObservable(): Observable<CubiqTS.InterventionEventImpl> {
    return this.interventionEventSubject.asObservable();
  }

  /* ******************************************************* */
  /* BMARK ORDER-DATA UPDATE PRIMARY SUBJECT */

  private processForPrivatization(
    orderData: Array<CubiqTS.OrderDataImpl> | CubiqTS.OrderDataImpl,
  ): void {
    let orderDataArray = null;
    if (orderData instanceof CubiqTS.OrderDataImpl) {
      orderDataArray = new Array<CubiqTS.OrderDataImpl>();
      orderDataArray.push(orderData);
    } else if (Array.isArray(orderData) && orderData.length > 0) {
      orderDataArray = orderData;
    }

    if (orderDataArray != null) {
      for (let orderDataItem of orderDataArray) {
        this.orderPrivatizationService.checkPrivatization(orderDataItem);
      }
    }
  }

  /* ******************************************************* */
  /* BMARK ORDER-DATA UPDATE EVENT SENDERS */
  /* Not in use */
  /*     protected fireBmarkBatchOrderDataUpdateEvent(batchUpdate: Array<any>): any {
        if (batchUpdate instanceof Array && batchUpdate.length > 0) {

            let $event = { subjectType: BmarkEventSubjectType.ORDER_DATA, data: batchUpdate};
            this.bmarkOrderDataUpdateSubject.next($event.data);
            this.logBmarkInfo(`Bmark-Lookup: Sending BATCH OrderData update... ${BmarkOrderDataEventOperation[BmarkOrderDataEventOperation.BATCH_UPDATE]} => `, $event);
        }else{
           logger.out.error(`ERROR: BMARK: fireFixingUpdateEvent(): Invalid BATCH update data:`, batchUpdate);
        }
    } */

  protected fireBmarkOrderDataUpdateEvent(orderDataEvent: BmarkOrderDataEvent): any {
    // operations: Array<BmarkOrderDataEventOperation>, bmarkOrderData: CubiqTS.OrderDataImpl | null, metaData: any
    if (
      orderDataEvent instanceof BmarkOrderDataEvent &&
      orderDataEvent.payload instanceof BmarkOrderDataEventPayload &&
      Array.isArray(orderDataEvent.operations) &&
      orderDataEvent.operations.length > 0
    ) {
      this.logBmarkInfo(
        `Bmark-Lookup: Sending BMARK-ORDER-DATA-EVENT ` +
          `(${
            orderDataEvent.payload.source in BmarkOrderDataEventSource
              ? BmarkOrderDataEventSource[orderDataEvent.payload.source]
              : "N/A"
          })` +
          ` update => operations: ${orderDataEvent.operations.map(
            (item) => BmarkOrderDataEventOperation[item],
          )} => `,
        orderDataEvent,
      );

      this.bmarkOrderDataUpdateSubject.next(orderDataEvent);
    } else {
      logger.out.error(
        `ERROR: BMARK: fireFixingUpdateEvent(): Invalid order-data-event => `,
        orderDataEvent,
      );
    }
  }

  /* ******************************************************* */
  /* ORDER DATA IMPL SERIALIZER */
  protected serializeOrderDataInUpdateEvent($event: any): void {
    try {
      if (Array.isArray($event)) {
        // for batched events
        for (let $eventItem of $event) {
          this.serializeOrderDataInUpdateEvent($eventItem);
        }
      } else {
        if (
          $event != null &&
          $event.data != null &&
          $event.data.payload != null &&
          $event.data.payload.orderData instanceof CubiqTS.OrderDataImpl
        ) {
          $event.data.payload.orderData = this.control.attachImplToEvent
            ? this._serializer.serialize($event.data.payload.orderData, this.VALUE)
            : null;
        }
      }
    } catch (ex) {
      logger.out.error(`ERROR: BMARK: serializeOrderDataInUpdateEvent(): `, ex);
    }
  }

  /* ******************************************************* */
  /* BMARK ORDER DATA EVENT HANDLERS */
  protected processBmarkOrderLifecycleEvent(
    event: CubiqTS.OrderLifeCycleEventImpl,
    returnDoNotSend: boolean = false,
  ): BmarkOrderDataEvent {
    if (event instanceof CubiqTS.OrderLifeCycleEventImpl) {
      try {
        logger.out.info(
          `${this.constructor.name}.processBmarkOrderLifecycleEvent(): OrderLifeCycleEventImpl orderId: ${event?.orderData?.orderId}`,
        );

        if (
          event.orderData != null &&
          this.shouldProcessOutsidePreVisibility(event.orderData as CubiqTS.OrderDataImpl, event)
        ) {
          this.processOutsidePreVisibility(event);
        } else if (
          event.orderData != null &&
          this.shouldProcessUpdate(event.orderData as CubiqTS.OrderDataImpl, event)
        ) {
          /* ORDER DATA */
          let orderDataUpdate: BmarkOrderData | null =
            event.orderData != null
              ? this.base.preprocessOrderData(event.orderData as BmarkOrderData)
              : null;
          let orderDataStored: BmarkOrderData = this.base.getBmarkOrderDataForOrderId(
            orderDataUpdate.orderId.toString(),
          );

          this.updateMissingNPVValues(orderDataStored, orderDataUpdate);

          /* EVENT */
          let orderDataEvent = new BmarkOrderDataEvent();
          /*************************************/
          /* PROCESSING  */
          this.orderPrivatizationService.checkPrivatization(orderDataUpdate);
          // process lock
          orderDataUpdate.lockType =
            orderDataUpdate.lockType === CubiqTS.LockType.CANCEL_LOCK
              ? null
              : orderDataUpdate.lockType;
          /*************************************/
          /* FLOW CONTROL */
          // UPDATE/DELETE
          // #bmarkVisibility
          let canSeeBmarkOrder = this.activeUser.canSeeBmarkOrder(orderDataUpdate);
          let activeFixingId = BmarkFixingData.generateFixingIdFromOrderData(
            orderDataUpdate,
            BmarkFixingType.ACTIVE,
          );
          let recentFixingId = BmarkFixingData.generateFixingIdFromOrderData(
            orderDataUpdate,
            BmarkFixingType.RECENT,
          );
          let isInRecentFixing = this.base.hasRecentFixing(recentFixingId);
          let isInRejectedList = BENCHMARK_REJECT_STATES.has(orderDataUpdate.orderState);
          let isInActiveList = BENCHMARK_ACTIVE_STATES.has(orderDataUpdate.orderState);
          // NOTIFICATIONS - Flow Control variables
          let determinedNotifTypes = this.notificationProcessor.determineBmarkNotifEvent(
            event,
            orderDataUpdate,
            orderDataStored,
          );
          let notifType: BFXNotificationType = determinedNotifTypes.type;
          let notifSubType: BFXNotificationSubType = determinedNotifTypes.subType;
          let appNotifType: BFXAppNotificationType = determinedNotifTypes.appType;

          let shouldNotifyFg: boolean = this.notificationService.shouldNotify(
            notifType,
            event.orderData.orderType,
          );
          let shouldAppNotifyFg: boolean = this.notificationService.shouldAppNotify(
            appNotifType,
            event.orderData.orderType,
          );

          // ORDER ENTRY - Flow Control variables
          let shouldSendOEClear =
            orderDataUpdate.orderState === CubiqTS.OrderState.IN_MODIFICATION ||
            orderDataUpdate.orderState === CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED;
          let isReject = event.eventTypeId === CubiqTS.FxLifeCycleEventType.REJECTED;
          let isRollbackEvent = event.rollbackEventFg;

          ///////////////////////////////////////////////////// DELETE FLOW
          if (!canSeeBmarkOrder || isInRejectedList || (!isInActiveList && !isInRecentFixing)) {
            /*************************************/
            /* UPDATE LOOKUP DATA */
            // perform delete if it exists
            if (orderDataUpdate instanceof CubiqTS.OrderDataImpl) {
              // update fixings
              this.processOrderDataDelete(orderDataUpdate);
            } else {
              logger.out.warn(
                `${this.constructor.name}.processBmarkOrderLifecycleEvent() failed to update fixing for order`,
              );
            }
            // store
            if (
              orderDataUpdate instanceof CubiqTS.OrderDataImpl &&
              !BENCHMARK_NEW_ORDER_STATES.has(orderDataUpdate.orderState)
            ) {
              let txTime = parseInt(orderDataUpdate.txTime.format("x"));
              this.base.addJustDeleted(orderDataUpdate.orderId.toString(), txTime);
            }
            /*************************************/
            /* DEFINE EVENT */
            /* define meta data */
            let eventPayloadData = new BmarkOrderDataEventPayload(
              orderDataUpdate.orderId.toString(),
              event.externalClientReference,
              BmarkOrderDataEventSource.OrderLifeCycleEvent,
              orderDataUpdate,
            );

            /* define operations */
            orderDataEvent.addOperations(BmarkOrderDataEventOperation.DELETE); // for blotters
            if (!isRollbackEvent) {
              // for order entry
              if (isReject) {
                eventPayloadData.errors = event.errors;
                orderDataEvent.addOperations(BmarkOrderDataEventOperation.OE_ERROR);
              } else if (BENCHMARK_NEW_ORDER_STATES.has(orderDataUpdate.orderState)) {
                // // bmark order data
                eventPayloadData.bmarkOrderData = orderDataUpdate; //orderDataUpdate;
                // meta-data
                eventPayloadData.state = orderDataUpdate.orderState;
                eventPayloadData.evtType = event.eventTypeId;
                eventPayloadData.messageId = event.messageId;
                eventPayloadData.sgcId =
                  !shouldSendOEClear && orderDataUpdate.salesGroupId != null
                    ? orderDataUpdate.salesGroupId.toString()
                    : null;
                eventPayloadData.isCreditUpdate =
                  event.eventTypeId == CubiqTS.FxLifeCycleEventType.CREDIT_UPDATE;
                eventPayloadData.isRollbackEvent = event.rollbackEventFg;
                // errors
                eventPayloadData.errors = event.errors;
                //debugger;
                orderDataEvent.addOperations(BmarkOrderDataEventOperation.OE_DI_NEEDED);
              }
            }
            /* attach payload */
            orderDataEvent.payload = eventPayloadData;
            /*************************************/
            /* NOTIFICATION EVENT */
            if (orderDataStored != null) {
              if (shouldNotifyFg) {
                this.notifService.fireNotificationEvent(
                  this.notifService.orderDataToNotification(
                    orderDataUpdate,
                    notifType,
                    notifSubType,
                  ),
                  BFXNotificationEventType.ORDER_UPDATE_EVENT,
                );
              } else {
                this.notifService.fireNotificationClearEvent(
                  this.notifService.dataToClearEvent([orderDataUpdate.orderId.toString()]),
                  BFXNotificationEventType.ORDER_UPDATE_EVENT,
                );
              }
              if (shouldAppNotifyFg) {
                this.notifService.fireAppNotificationEvent(
                  this.notifService.orderDataToNotification(orderDataUpdate, notifType),
                  BFXAppNotificationEventType.ORDER_UPDATE_EVENT,
                );
              }
            }
            ///////////////////////////////////////////////////// UPDATE FLOW
          } else {
            /*************************************/
            /* UPDATE LOOKUP DATA */
            switch (event.fxOrderLifeCycleEvent) {
              case CubiqTS.FxLifeCycleEventType.MODIFICATION_INFO: // MODIFY INFO
                if (orderDataStored != null) {
                  orderDataStored.comments1 = orderDataUpdate.comments1;
                  orderDataStored.comments2 = orderDataUpdate.comments2;
                  orderDataStored.clientTraderName = orderDataUpdate.clientTraderName;
                }
                break;
              case CubiqTS.FxLifeCycleEventType.CREDIT_UPDATE:
                if (orderDataStored != null) {
                  orderDataStored.creditRevalDetails = orderDataUpdate.creditRevalDetails;
                  this.base.updateItem(orderDataStored);
                }
                break;
              // case CubiqTS.FxLifeCycleEventType.SI_REQUIRED:
              //   if (orderDataStored != null){
              //     orderDataStored.creditRevalDetails = orderDataUpdate.creditRevalDetails;
              //     this.base.updateItem(orderDataStored);
              //   }
              //     break;
              default:
                if (
                  orderDataStored != null &&
                  orderDataUpdate != null &&
                  orderDataStored.orderRevisionId != null &&
                  orderDataUpdate.orderRevisionId != null &&
                  !orderDataStored.orderRevisionId.equals(orderDataUpdate.orderRevisionId)
                ) {
                  let deleteOrderDataEvent: BmarkOrderDataEvent = new BmarkOrderDataEvent();
                  deleteOrderDataEvent.addOperations(BmarkOrderDataEventOperation.DELETE);

                  let payload: BmarkOrderDataEventPayload = new BmarkOrderDataEventPayload(
                    orderDataUpdate.orderId.toString(),
                    event.externalClientReference,
                    BmarkOrderDataEventSource.OrderLifeCycleEvent,
                    orderDataUpdate,
                  );
                  deleteOrderDataEvent.payload = payload;
                  this.processOrderDataUpdateForFixings(orderDataUpdate, true);
                  this.fireBmarkOrderDataUpdateEvent(deleteOrderDataEvent);
                }

                orderDataStored = orderDataUpdate;
                this.base.mapItem(orderDataStored);
                if (!shouldNotifyFg) {
                  this.fireModelNotificationClearEvent([orderDataUpdate.orderId.toString()]);
                }
                break;
            }
            // update fixings
            if (BENCHMARK_ACTIVE_STATES.has(orderDataUpdate.orderState)) {
              // fix for OMS-30944 - fixing time changed
              let existingFixingId = this.base.getActiveFixingIdForOrderId(
                orderDataUpdate.orderId.toString(),
              );
              if (existingFixingId != null && existingFixingId != activeFixingId) {
                this.processOrderDataUpdateForFixings(orderDataUpdate, true, existingFixingId);
                // delete the old order in blotter
                let deleteOrderDataEvent: BmarkOrderDataEvent = new BmarkOrderDataEvent();
                deleteOrderDataEvent.addOperations(BmarkOrderDataEventOperation.DELETE);

                let payload: BmarkOrderDataEventPayload = new BmarkOrderDataEventPayload(
                  orderDataUpdate.orderId.toString(),
                  event.externalClientReference,
                  BmarkOrderDataEventSource.OrderLifeCycleEvent,
                  orderDataUpdate,
                  existingFixingId,
                );
                deleteOrderDataEvent.payload = payload;
                this.fireBmarkOrderDataUpdateEvent(deleteOrderDataEvent);
              }
              this.processOrderDataUpdateForFixings(orderDataUpdate);
            } else if (this.base.hasRecentFixing(recentFixingId)) {
              this.processOrderDataUpdateForFixings(orderDataUpdate);
            }

            /*************************************/
            /* META-DATA PREPARATION */
            let lockUser: any = null;
            if (shouldSendOEClear) {
              lockUser = orderDataUpdate.lockUserId ? orderDataUpdate.lockUserId.toString() : null;
            } else if (CubiqTS.FxLifeCycleEventType.SI_REQUIRED && orderDataUpdate.siLockUserId) {
              lockUser = orderDataUpdate.siLockUserId.toString();
            } else if (orderDataUpdate.lockUserId) {
              lockUser = orderDataUpdate.lockUserId.toString();
            }

            // traderGroups
            let traderGroupIds: Array<string> = new Array<string>();
            if (!shouldSendOEClear) {
              if (orderDataUpdate.deskId != null) {
                traderGroupIds.push(orderDataUpdate.deskId.toString());
              }
              if (
                traderGroupIds.length === 0 &&
                Array.isArray(orderDataUpdate.ownerGroupIds) &&
                orderDataUpdate.ownerGroupIds[0] instanceof CubiqTS.Long
              ) {
                traderGroupIds = orderDataUpdate.ownerGroupIds.map((item) => item.toString());
              }
            }
            /*************************************/
            /* DEFINE EVENT */
            /* define payload */
            let eventPayloadData = new BmarkOrderDataEventPayload(
              orderDataUpdate.orderId.toString(),
              event.externalClientReference != null
                ? event.externalClientReference
                : orderDataUpdate.clOrdID,
              BmarkOrderDataEventSource.OrderLifeCycleEvent,
              orderDataUpdate,
            );
            // bmark order data
            eventPayloadData.bmarkOrderData =
              this.base.processLocalBmarkOrderDataForVisibility(orderDataUpdate); //orderDataUpdate;
            // meta-data
            eventPayloadData.state = orderDataUpdate.orderState;
            eventPayloadData.evtType = event.eventTypeId;
            eventPayloadData.messageId = event.messageId;
            eventPayloadData.sgcId =
              !shouldSendOEClear && orderDataUpdate.salesGroupId != null
                ? orderDataUpdate.salesGroupId.toString()
                : null;
            eventPayloadData.tgcId = traderGroupIds;
            (eventPayloadData.deskCode =
              !shouldSendOEClear && Array.isArray(traderGroupIds) && traderGroupIds.length > 0
                ? traderGroupIds.map((item) => {
                    let tgc = this.traderGroupService.getItem(item.toString());
                    return tgc instanceof CubiqTS.TradingGroupImpl
                      ? tgc.groupCode
                      : item.toString();
                  })
                : new Array<string>()),
              (eventPayloadData.user = lockUser);
            eventPayloadData.isCreditUpdate =
              event.eventTypeId == CubiqTS.FxLifeCycleEventType.CREDIT_UPDATE;
            eventPayloadData.isRollbackEvent = event.rollbackEventFg;
            // errors
            eventPayloadData.errors = event.errors;

            /* define operations */
            orderDataEvent.addOperations(BmarkOrderDataEventOperation.UPDATE);
            if (shouldSendOEClear) {
              orderDataEvent.addOperations(BmarkOrderDataEventOperation.OE_CLEAR);
            }

            /* attach payload */
            orderDataEvent.payload = eventPayloadData;
            /*************************************/
            /* FIRE NOTIFICATION EVENT */
            if (shouldNotifyFg) {
              this.notifService.fireNotificationEvent(
                this.notifService.orderDataToNotification(orderDataStored, notifType, notifSubType),
                BFXNotificationEventType.ORDER_UPDATE_EVENT,
              );
            }
            if (shouldAppNotifyFg) {
              this.notifService.fireAppNotificationEvent(
                this.notifService.orderDataToNotification(orderDataStored, notifType),
                BFXAppNotificationEventType.ORDER_UPDATE_EVENT,
              );
            }
          }
          /*************************************/
          /* FIRE BMARK EVENT */
          if (!returnDoNotSend) {
            this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
            // for dialogs like cancel -dc
            // this.fireModelUpdateEvent(event.orderData);
            // fireModelUpdateEvent is broken from here?
            let eventObject: any = Object.assign(
              Object.create(BmarkOrderData.prototype),
              orderDataUpdate,
              {
                source: OrderDataModelUpdateSource.OrderLifeCycleEvent,
                evtTypeId: event.eventTypeId,
              },
            );
            if (
              event.errors != null &&
              Array.isArray(event.errors.errorMessages) &&
              event.errors.errorMessages.length > 0
            ) {
              eventObject.orderErrors = event.errors;
            }
            this.modelUpdateEventSubject.next(eventObject);
          }

          return orderDataEvent;
        } else {
          logger.out.warn(
            `Not processing event as older than current. orderId: ${event?.orderData?.orderId}`,
          );
        }
      } catch (error) {
        logger.out.error(
          `Failed to propagate OrderLifeCycleEvent event. orderId: ${event?.orderData?.orderId} %o`,
          error,
        );
      }
    } else {
      logger.out.error(`Invalid OrderLifeCycleEvent event %o`, event);
    }
    return null;
  }

  private processOutsidePreVisibility(event: CubiqTS.OrderLifeCycleEventImpl) {
    let orderDataUpdate: BmarkOrderData | null =
      event.orderData != null
        ? this.base.preprocessOrderData(event.orderData as BmarkOrderData)
        : null;
    let orderDataStored: BmarkOrderData = this.base.getBmarkOrderDataForOrderId(
      orderDataUpdate.orderId.toString(),
    );
    const fixingSourceCd = orderDataStored?.fixingSourceCd ?? orderDataUpdate?.fixingSourceCd;
    const previousFixingDts = orderDataUpdate.previousFixingDts;

    if (
      fixingSourceCd != null &&
      typeof fixingSourceCd === "string" &&
      CubiqTS.moment.isMoment(previousFixingDts)
    ) {
      const activeFixingId = BmarkFixingData.generateFixingIdFromCodeMoment(
        fixingSourceCd,
        previousFixingDts,
        BmarkFixingType.ACTIVE,
      );

      let eventObject: BmarkOrderData;
      let orderDataEvent = new BmarkOrderDataEvent();
      let eventPayloadData = new BmarkOrderDataEventPayload(
        orderDataUpdate.orderId.toString(),
        event.externalClientReference,
        BmarkOrderDataEventSource.OrderLifeCycleEvent,
        orderDataUpdate,
        activeFixingId,
      );

      logger.out.info(
        `${this.constructor.name}.processOutsidePreVisibility() for orderId: ${orderDataUpdate?.orderId} outside pre-fixing visibility.`,
      );

      this.processOrderDataDelete(orderDataUpdate, activeFixingId);
      orderDataEvent.addOperations(BmarkOrderDataEventOperation.DELETE);
      orderDataEvent.payload = eventPayloadData;
      this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
      eventObject = Object.assign(Object.create(BmarkOrderData.prototype), orderDataUpdate, {
        source: OrderDataModelUpdateSource.OrderLifeCycleEvent,
        evtTypeId: event.eventTypeId,
      });
      if (
        event.errors != null &&
        Array.isArray(event.errors.errorMessages) &&
        event.errors.errorMessages.length > 0
      ) {
        eventObject.orderErrors = event.errors;
      }
      this.modelUpdateEventSubject.next(eventObject);
    } else {
      logger.out.warn(
        `${this.constructor.name}.processOutsidePreVisibility() failed to update fixing for orderId: ${orderDataUpdate?.orderId} outside pre-fixing visibility.`,
      );
    }
  }

  private processOrderDataDelete(orderData: BmarkOrderData, activeFixingId: string = null) {
    logger.out.info(
      `${this.constructor.name}.processOrderDataDelete() orderId: ${orderData?.orderId} activeFixingId: ${activeFixingId}`,
    );
    this.processOrderDataUpdateForFixings(orderData, true, activeFixingId);
    // update map
    this.base.deleteItemById(orderData.orderId.toString());
    this.fireModelDeleteEvent(orderData.orderId.toString());
  }

  /* BMARK LOCK EVENT HANDLER */
  protected processBmarkLockLifecycleEvent(event: CubiqTS.BenchmarkLockLifeCycleEventImpl): any {
    if (event instanceof CubiqTS.BenchmarkLockLifeCycleEventImpl) {
      try {
        if (
          event.lockType != CubiqTS.LockType.CANCEL_LOCK &&
          event.orderIds != null &&
          Array.isArray(event.orderIds)
        ) {
          let batchUpdate: Array<BmarkOrderDataEvent> = new Array<BmarkOrderDataEvent>();

          for (let orderId of event.orderIds) {
            /* ORDER-DATA */
            let orderDataStored: BmarkOrderData = this.base.getBmarkOrderDataForOrderId(
              orderId.toString(),
            );
            /* EVENT  */
            let orderDataEvent = new BmarkOrderDataEvent();
            /* PROCESSING  */
            if (orderDataStored instanceof CubiqTS.OrderDataImpl) {
              /*************************************/
              /* UPDATE LOOKUP DATA */
              if (
                event.abort === true ||
                event.eventTypeId === CubiqTS.FxLifeCycleEventType.UNLOCKED
              ) {
                // UNLOCK
                orderDataStored.lockType = null;
                orderDataStored.lockUserId = null;
                orderDataStored.orderState = CubiqTS.OrderState.ACTIVE;
              } else {
                // LOCK
                orderDataStored.lockType = event.lockType;
                orderDataStored.lockUserId = event.lockUserId;
                if (event.lockType == CubiqTS.LockType.MODIFY_LOCK) {
                  orderDataStored.orderState = CubiqTS.OrderState.IN_MODIFICATION;
                }
              }
              this.base.updateItem(orderDataStored);

              /*************************************/
              /* DEFINE EVENT */
              let eventPayloadData = new BmarkOrderDataEventPayload(
                orderId.toString(),
                orderDataStored.clOrdID,
                BmarkOrderDataEventSource.BmarkLockLifeCycleEvent,
                orderDataStored,
              );
              // order data
              eventPayloadData.bmarkOrderData = orderDataStored;
              eventPayloadData.state = orderDataStored.orderState;
              // meta-data
              eventPayloadData.evtType = event.eventTypeId;
              eventPayloadData.messageId = event.messageId;
              eventPayloadData.user = event.lockUserId ? event.lockUserId.toString() : null;
              eventPayloadData.lockEvent = true;

              if (
                event.errors != null &&
                Array.isArray(event.errors.errorMessages) &&
                event.errors.errorMessages.length > 0
              ) {
                eventPayloadData.errors = event.errors;
              }

              /* define operations */
              orderDataEvent.addOperations([
                BmarkOrderDataEventOperation.UPDATE,
                BmarkOrderDataEventOperation.LOCK_UPDATE,
              ]);
              /* attach payload */
              orderDataEvent.payload = eventPayloadData;

              /*************************************/
              /* FIRE BMARK UPDATE EVENT */
              this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
            }
          }
        }
      } catch (error) {
        logger.out.error("Failed to propagate BenchmarkLockLifeCycleEventImpl event.", error);
      }
    } else {
      logger.out.error("Invalid BenchmarkLockLifeCycleEventImpl event.", event);
    }
  }

  /* TRANSFER EVENT HANDLER */
  protected processBmarkXferEvent(event: CubiqTS.XferEventImpl): void {
    if (event instanceof CubiqTS.XferEventImpl) {
      try {
        let batchUpdate: Array<any> = [];
        let clearNotifs: Array<string> = [];
        this.logBmarkInfo(`BMARK: XferEventImpl.count[${event.orderData.length}]`);

        for (let orderData of event.orderData) {
          if (orderData != null && this.shouldProcessUpdate(orderData as CubiqTS.OrderDataImpl)) {
            /* ORDER DATA */
            let orderDataUpdate: BmarkOrderData | null =
              orderData != null ? this.base.preprocessOrderData(orderData as BmarkOrderData) : null;
            /* EVENT  */
            let orderDataEvent = new BmarkOrderDataEvent();
            /* PROCESSING  */
            this.orderPrivatizationService.checkPrivatization(orderDataUpdate);

            /*************************************/
            /* FLOW CONTROL */
            // UPDATE/DELETE
            let canSeeBmarkOrder = this.activeUser.canSeeBmarkOrder(orderDataUpdate);
            let recentFixingId = BmarkFixingData.generateFixingIdFromOrderData(
              orderDataUpdate,
              BmarkFixingType.RECENT,
            );
            let isInRecentFixing = this.base.hasRecentFixing(recentFixingId);
            let isInRejectedList = BENCHMARK_REJECT_STATES.has(orderDataUpdate.orderState);
            let isInActiveList = BENCHMARK_ACTIVE_STATES.has(orderDataUpdate.orderState);
            let isDeleted = false;

            /*************************************/
            /* META-DATA PREPARATION */
            let username: string = null;
            if (event.actioningUserId != null) {
              let user: UserImpl = this.userService.getUserById(event.actioningUserId.toString());
              username = user != null ? user.userName : username;
            }

            let targetGroup: string = null;
            if (event.targetGroupId != null) {
              let tradeGroup: TradingGroupImpl = this.traderGroupService.getItem(
                event.targetGroupId.toString(),
              );
              targetGroup = tradeGroup != null ? tradeGroup.groupCode : targetGroup;
            }

            /*************************************/
            /* DEFINE EVENT */
            let eventPayloadData = new BmarkOrderDataEventPayload(
              orderDataUpdate.orderId.toString(),
              orderDataUpdate.clOrdID,
              BmarkOrderDataEventSource.XferEvent,
              orderDataUpdate,
            );
            // order data
            eventPayloadData.bmarkOrderData = orderDataUpdate;
            // meta-data
            eventPayloadData.messageId = event.messageId;
            eventPayloadData.xferEventType = event.eventType;
            eventPayloadData.xferComment = event.comment;
            eventPayloadData.xferUserName = username;
            eventPayloadData.xferTargetGroup = targetGroup;

            if (!canSeeBmarkOrder || isInRejectedList || (!isInActiveList && !isInRecentFixing)) {
              /*************************************/
              /* UPDATE LOOKUP DATA */
              if (this.base.hasItem(orderDataUpdate.orderId.toString())) {
                // perform delete if it exists
                // update fixings
                this.processOrderDataUpdateForFixings(orderDataUpdate, true);
                // update map
                this.base.deleteItemById(orderDataUpdate.orderId.toString());
              }

              eventPayloadData.deleteReason = TRANSFERED_OUT_OF_VISIBILITY;
              /* define operations */
              orderDataEvent.addOperations(BmarkOrderDataEventOperation.DELETE);
              isDeleted = true;
            } else {
              /*************************************/
              /* UPDATE LOOKUP DATA */
              this.base.mapItem(orderDataUpdate as CubiqTS.OrderDataImpl);
              // update fixings
              if (BENCHMARK_ACTIVE_STATES.has(orderDataUpdate.orderState)) {
                this.processOrderDataUpdateForFixings(orderDataUpdate);
              }

              // order data update with visibility - fix for
              eventPayloadData.bmarkOrderData =
                this.base.processLocalBmarkOrderDataForVisibility(orderDataUpdate);
              this.devTestDisplayOrderData([eventPayloadData.bmarkOrderData]);
              /* define operations */
              orderDataEvent.addOperations(BmarkOrderDataEventOperation.UPDATE);
            }
            /* attach payload */
            orderDataEvent.payload = eventPayloadData;
            /*************************************/
            /* FIRE BMARK EVENT */
            this.fireBmarkOrderDataUpdateEvent(orderDataEvent);

            if (isDeleted) {
              this.fireModelDeleteEvent(orderDataUpdate.orderId.toString());

              // Create correct transfer ID for a BM order and attach to the event
              const xferData = TransferUtils.getXferEventAdditionalData(
                event,
                this.userService,
                this.traderGroupService,
              );
              xferData.xferId = TransferUtils.getTransferId(orderData, true);

              this.fireModelDeleteWithReasonEvent(
                orderData.orderId.toString(),
                TRANSFERED_OUT_OF_VISIBILITY,
                orderData,
                xferData,
              );
            } else {
              this.fireModelUpdateEventForTransfer(
                eventPayloadData.bmarkOrderData,
                event,
                username,
                targetGroup,
              );
            }
          }
        } // for end
        /*************************************/
        /* FIRE BMARK BATCH UPDATE EVENT */

        if (clearNotifs.length > 0) {
          this.fireModelNotificationClearEvent(clearNotifs);
        }
      } catch (error) {
        logger.out.error("Failed to propagate processXferEvent event.", error);
      }
    } else {
      logger.out.error("Invalid processXferEvent event.", event);
    }
  }

  /**
   * Handle a single Netting event
   * @param event
   */
  protected processManualNettingEvent(event: CubiqTS.ManualNettingEventImpl): void {
    if (event.orderIds != null && Array.isArray(event.orderIds)) {
      event.orderIds.forEach((orderId) => {
        if (orderId != null) {
          let order: CubiqTS.OrderDataImpl = this.base.getItem(orderId);
          if (order != null) {
            const fwdNettingId = event.hasPortfolioId ? event.portfolioId : null;
            this.updateSingleNettingId(orderId, fwdNettingId);
          }
        }
      });
    }
  }

  /**
   * Handle a single Netting Bulk event
   * @param event
   */
  protected processManualNettingBulkEvent(event: CubiqTS.ManualNettingBulkEventImpl): void {
    if (event.nettingPairs != null && Array.isArray(event.nettingPairs)) {
      event.nettingPairs.forEach((nettingPair) => {
        if (nettingPair != null) {
          const orderId = nettingPair.orderId;
          const portfolioId = nettingPair.portfolioId;
          this.updateSingleNettingId(orderId, portfolioId);
        }
      });
    }
  }

  /**
   * Update the portfolio ID for the specified order, if found, and output a NETTING_UPDATE event.
   * Currently, this event type is not used by Order Entry as portfolio Id should be tranparently propagated
   * on the server
   * @param orderId
   * @param portfolioId
   */
  private updateSingleNettingId(orderId: string, portfolioId: string): void {
    logger.out.info(`oebmd-s - updating nettingId ${portfolioId} for order ${orderId}`);
    let existing: BmarkOrderData = this.base.getBmarkOrderDataForOrderId(orderId);
    if (existing instanceof CubiqTS.OrderDataImpl) {
      existing.fwdNettingId = portfolioId;
      this.base.updateItem(existing);

      let eventPayloadData = new BmarkOrderDataEventPayload(
        orderId,
        existing.clOrdID,
        BmarkOrderDataEventSource.NettingEvent,
        existing,
      );
      eventPayloadData.bmarkOrderData = existing;
      eventPayloadData.state = existing.orderState;

      /* define operations */
      let orderDataEvent = new BmarkOrderDataEvent();
      orderDataEvent.addOperations([BmarkOrderDataEventOperation.NETTING_UPDATE]);
      orderDataEvent.payload = eventPayloadData;
      this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
    }
  }

  private fireModelUpdateEventForTransfer(
    bmarkOrderData: BmarkOrderData,
    event: CubiqTS.XferEventImpl,
    username: string,
    targetGroup: string,
  ) {
    const xferData = TransferUtils.getXferEventAdditionalData(
      event,
      this.userService,
      this.traderGroupService,
    );
    xferData.xferId = TransferUtils.getTransferId(bmarkOrderData, true);

    let eventObject: any = Object.assign(
      Object.create(BmarkOrderData.prototype),
      bmarkOrderData,
      xferData,
    );
    this.modelUpdateEventSubject.next(eventObject);
  }

  /* BULK LIFE-CYCLE EVENT HANDLER */
  protected processBmarkBulkOrderLifecycleEvent(event: CubiqTS.BulkOrderLifeCycleEventImpl): void {
    if (event instanceof CubiqTS.BulkOrderLifeCycleEventImpl) {
      try {
        for (let iEvent of event.events) {
          if (iEvent instanceof CubiqTS.OrderLifeCycleEventImpl && this.benchmarksFilter(iEvent)) {
            this.processBmarkOrderLifecycleEvent(iEvent);
          }
        }
      } catch (error) {
        logger.out.error("Failed to propagate BulkOrderLifeCycleEventImpl event.", error);
      }
    } else {
      logger.out.error("Invalid BulkOrderLifeCycleEventImpl event.", event);
    }
  }

  protected processUnsolCanLifecycleEvent(event: CubiqTS.UnsolicitedCancelLifeCycleEventImpl): any {
    event.orders.forEach((incoming: CubiqTS.OrderData) => {
      if (event._extendedInfo.get("makerId") != null) {
        let makerId = event._extendedInfo.get("makerId");
        let makerIdNum: number = +makerId;
        incoming.deskId = new CubiqTS.Long(makerIdNum);
      }
      if (incoming != null) {
        if (
          this.activeUser.isBenchmarkOrder(incoming) &&
          this.activeUser.canSeeBmarkOrder(incoming, false)
        ) {
          // NOTIFICATIONS - Flow Control variables
          let notifType: BFXNotificationType = BFXNotificationType.UNSOLICITED_CANCEL;
          let shouldNotifyFg: boolean = this.notifService.shouldNotify(
            BFXNotificationType.UNSOLICITED_CANCEL,
            incoming.orderType,
          );
          let makerName: string = event._extendedInfo.get("maker");
          let opsId: string = event._extendedInfo.get("opsId");
          if (shouldNotifyFg) {
            this.notifService.fireNotificationEvent(
              this.notifService.orderDataToUnsolCanNotification(
                incoming,
                notifType,
                event.alternateGroupId?.toString() ?? "",
                event.alternateGroupName,
                makerName,
                opsId,
              ),
              BFXNotificationEventType.ORDER_UPDATE_EVENT,
            );
          } else {
            this.notifService.fireNotificationClearEvent(
              this.notifService.dataToClearEvent([incoming.orderId.toString()]),
              BFXNotificationEventType.ORDER_UPDATE_EVENT,
            );
          }
        }
      }
    });
  }

  /* INTERVENTION EVENT HANDLER */
  protected processBmarkInterventionEvent($event: CubiqTS.InterventionEventImpl): void {
    if ($event instanceof CubiqTS.InterventionEventImpl) {
      try {
        if (
          $event.eventType !== CubiqTS.InterventionEventType.LIMIT_ORDER &&
          $event.orderData instanceof CubiqTS.OrderDataImpl &&
          this.shouldProcessUpdate($event.orderData as CubiqTS.OrderDataImpl)
        ) {
          if ($event.orderData && $event.orderData instanceof CubiqTS.OrderDataImpl) {
            $event.orderData = this.orderPrivatizationService.checkPrivatization(
              $event.orderData as CubiqTS.OrderDataImpl,
            );
          }

          /*************************************/
          /* ORDER DATA */
          let orderDataUpdate: CubiqTS.OrderDataImpl | null =
            $event.orderData != null ? $event.orderData : null;
          // allocations are not sent with OrderData in InterventionEventImpl, hence the preprocessing (line below) can not be used
          // this.base.preprocessOrderData($event.orderData as CubiqTS.OrderDataImpl) : null;
          /* EVENT */
          let orderDataEvent = new BmarkOrderDataEvent();

          /*************************************/
          /* FLOW CONTROL */
          let isEvtTypeNew = $event.operationType === CubiqTS.InterventionOperationType.NEW;
          let isEvtTypeApprove = $event.operationType === CubiqTS.InterventionOperationType.APPROVE;
          let isEvtTypeRejectHasDelete =
            $event.operationType === CubiqTS.InterventionOperationType.REJECT &&
            CubiqTS.OrderState.ACTIVE !== $event.orderData.orderState;
          let isEvtTypeRejectCancellation =
            $event.operationType === CubiqTS.InterventionOperationType.REJECT &&
            CubiqTS.OrderState.ACTIVE === $event.orderData.orderState;

          /*************************************/
          /* META-DATA PREPARATION */
          let traderGroupCodeId =
            $event.orderData != null && $event.orderData.deskId != null
              ? $event.orderData.deskId.toString()
              : null;
          if (
            traderGroupCodeId == null &&
            Array.isArray($event.orderData.allocationData) &&
            $event.orderData.allocationData.length > 0
          ) {
            // first allocation TG
            traderGroupCodeId =
              $event.orderData.allocationData[0].deskId instanceof CubiqTS.Long
                ? $event.orderData.allocationData[0].deskId.toString()
                : null;
          }
          let salesGroupCodeId =
            $event.orderData != null && $event.orderData.salesGroupId != null
              ? $event.orderData.salesGroupId.toString()
              : null;
          let lockUser = $event.lockUserId != null ? $event.lockUserId.toString() : null;

          let orderDataStored: BmarkOrderData = this.base.getBmarkOrderDataForOrderId(
            orderDataUpdate.orderId.toString(),
          );
          if (null != orderDataStored && null == lockUser) {
            orderDataStored.lockUserId = null;
            orderDataStored.lockType = null;
            this.base.updateItem(orderDataStored);
          }

          /*************************************/
          /* EVENT DEFINITION */
          let eventPayloadData = new BmarkOrderDataEventPayload(
            orderDataUpdate.orderId.toString(),
            orderDataUpdate.clOrdID,
            BmarkOrderDataEventSource.InterventionEvent,
            orderDataUpdate,
          );
          // order data
          eventPayloadData.bmarkOrderData = orderDataStored;
          //
          eventPayloadData.tgcId = isEvtTypeNew ? traderGroupCodeId : null;
          eventPayloadData.sgcId = isEvtTypeNew ? salesGroupCodeId : null;
          eventPayloadData.user = isEvtTypeNew ? lockUser : null;
          eventPayloadData.invEvtType = $event.eventType;
          eventPayloadData.invOprType = $event.operationType;
          eventPayloadData.reasonId = $event.reason;
          eventPayloadData.unlockUserId =
            $event.unLockUserId != null ? $event.unLockUserId.toString() : null;
          eventPayloadData.messageId = $event.messageId;
          eventPayloadData.errors = $event.errors;
          /* attach payload */
          orderDataEvent.payload = eventPayloadData;

          /* define operations */
          // order entry + blotters
          if (isEvtTypeNew) {
            orderDataEvent.addOperations(BmarkOrderDataEventOperation.OE_DI_NEW);
          } else if (isEvtTypeApprove) {
            orderDataEvent.addOperations(BmarkOrderDataEventOperation.OE_DI_APPROVE);
          } else if (isEvtTypeRejectHasDelete) {
            orderDataEvent.addOperations(BmarkOrderDataEventOperation.DELETE);
            orderDataEvent.addOperations(BmarkOrderDataEventOperation.OE_DI_REJECT);
          } else if (isEvtTypeRejectCancellation) {
            orderDataEvent.addOperations(BmarkOrderDataEventOperation.OE_DI_REJECT);
          }

          /*************************************/
          /* FIRE BMARK EVENT */
          this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
        } else {
          logger.out.warn(`Not processing event as older than current order.`, $event);
        }
      } catch (ex) {
        logger.out.error("ERROR: BMARK: Failed to propagate InterventionEventImpl event. ", ex);
      }
    } else {
      logger.out.error(`ERROR: BMARK: Invalid InterventionEventImpl event. %O`, $event);
    }
  }

  /* TRADE/DEAL EVENT HANDLER */
  protected processBmarkDeal(deal: CubiqTS.DealImpl): void {
    let orderId: string = deal.orderId != null ? deal.orderId.toString() : null;

    if (orderId != null) {
      let storedOrderData: CubiqTS.OrderDataImpl = this.base.getItem(orderId);
      if (storedOrderData != null) {
        if (storedOrderData.allocationData != null) {
          storedOrderData.allocationData.forEach((a) =>
            this.updateAllocationDealData(storedOrderData, a, deal),
          );
          storedOrderData = this.base.getBmarkOrderDataForOrderId(orderId);
          let updateOrderDataEvent: BmarkOrderDataEvent = new BmarkOrderDataEvent();
          updateOrderDataEvent.addOperations(BmarkOrderDataEventOperation.UPDATE);

          let payload: BmarkOrderDataEventPayload = new BmarkOrderDataEventPayload(
            orderId.toString(),
            storedOrderData.clOrdID,
            BmarkOrderDataEventSource.OrderLifeCycleEvent,
            storedOrderData,
          );
          updateOrderDataEvent.payload = payload;

          let canSeeBmarkOrder = this.activeUser.canSeeBmarkOrder(storedOrderData);
          let isInRejectedList = BENCHMARK_REJECT_STATES.has(storedOrderData.orderState);

          if (canSeeBmarkOrder && !isInRejectedList) {
            payload.bmarkOrderData = this.base.processLocalBmarkOrderDataForVisibility(
              storedOrderData as BmarkOrderData,
            );
            this.devTestDisplayOrderData([payload.bmarkOrderData]);
            this.processOrderDataUpdateForFixings(payload.bmarkOrderData);
            this.fireBmarkOrderDataUpdateEvent(updateOrderDataEvent);

            // NOTIFICATION
            this.dispatchBmarkFillNotification(deal);
          } else {
            this.processOrderDataUpdateForFixings(storedOrderData, true);
            let deleteOrderDataEvent: BmarkOrderDataEvent = new BmarkOrderDataEvent();
            deleteOrderDataEvent.addOperations(BmarkOrderDataEventOperation.DELETE);

            let payload: BmarkOrderDataEventPayload = new BmarkOrderDataEventPayload(
              storedOrderData.orderId.toString(),
              storedOrderData.clOrdID,
              BmarkOrderDataEventSource.OrderLifeCycleEvent,
              storedOrderData,
            );
            deleteOrderDataEvent.payload = payload;
            this.fireBmarkOrderDataUpdateEvent(deleteOrderDataEvent);
          }
        }
      }
    }
  }

  private updateAllocationDealData(
    storedOrderData: CubiqTS.OrderDataImpl,
    allocation: CubiqTS.AllocationDataImpl,
    deal: CubiqTS.DealImpl,
  ): void {
    if (deal.transactTime != null && deal.transactTime.isAfter(storedOrderData.txTime)) {
      let fillDetails: CubiqTS.LastFillDetailsImpl =
        allocation.fillDetails as CubiqTS.LastFillDetailsImpl;

      if (fillDetails != null) {
        fillDetails.dealState = deal.dealStateId;

        // Don't update deal state if this is a spot deal on forward order
        if (
          allocation.allocationProductCd === CubiqTS.Product.FWD &&
          deal.productType === CubiqTS.Product[CubiqTS.Product.SPT]
        ) {
          logger.out.info(
            `${this.constructor.name}: Ignoring deal state update for spot deal ` +
              `on forward order ${storedOrderData.orderId.toString()}`,
          );
          fillDetails.dealState = undefined;
        }
      }

      if (allocation.componentAllocations != null) {
        let allocationData: Array<CubiqTS.AllocationDataImpl> = allocation.componentAllocations;
        allocationData.forEach((a) => this.updateAllocationDealData(storedOrderData, a, deal));
      }
    }
  }

  /* FIXING ORDER UPDATE EVENT HANDLER (Fill Error)*/
  protected processBmarkFixingOrderUpdate($event: CubiqTS.FixingOrderUpdateEventImpl) {
    if (
      $event instanceof CubiqTS.FixingOrderUpdateEventImpl &&
      typeof $event.orderId === "string"
    ) {
      try {
        if ($event.orderState === CubiqTS.OrderState.FILL_ERROR) {
          /* ORDER DATA */
          let orderDataStored: BmarkOrderData = this.base.getBmarkOrderDataForOrderId(
            $event.orderId,
          );
          /* EVENT */
          let orderDataEvent = new BmarkOrderDataEvent();

          if (orderDataStored != null) {
            /*************************************/
            /* UPDATE LOOKUP DATA */
            orderDataStored.orderState = $event.orderState;
            this.base.updateItem(orderDataStored);

            /*************************************/
            /* DEFINE EVENT */
            /* define meta data */
            let eventPayloadData = new BmarkOrderDataEventPayload(
              orderDataStored.orderId.toString(),
              orderDataStored.clOrdID,
              BmarkOrderDataEventSource.OrderLifeCycleEvent,
              orderDataStored,
            );
            // bmark order data
            eventPayloadData.bmarkOrderData = orderDataStored;
            eventPayloadData.lockEvent = true;
            /* define operations */
            orderDataEvent.addOperations(BmarkOrderDataEventOperation.UPDATE);

            /* attach payload */
            orderDataEvent.payload = eventPayloadData;
            /*************************************/
            /* FIRE BMARK UPDATE EVENT */
            this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
          }
        } else {
          logger.out.warn(
            `WARN: BMARK: Received FixingOrderUpdateEventImpl with state other than FILL_ERROR`,
            $event,
          );
        }
      } catch (ex) {
        logger.out.error(
          "ERROR: BMARK: Failed to propagate FixingOrderUpdateEventImpl event. ",
          ex,
        );
      }
    }
  }

  protected processFixingCompleteEventForExpiredOrders(fixingEvent: CubiqTS.FixingEventImpl) {
    if (
      fixingEvent.fixingState === CubiqTS.FixingState.COMPLETED &&
      Array.isArray(fixingEvent.expiringOrderIds) &&
      fixingEvent.expiringOrderIds.length > 0
    ) {
      // Update order state to expired
      this.base.updateOrderDataState(fixingEvent.expiringOrderIds, CubiqTS.OrderState.FILL_EXPIRED);

      let orderDataUpdateForFixings: Array<BmarkOrderData> = [];

      // Create and dispatch OrderData events
      for (let orderId of fixingEvent.expiringOrderIds) {
        let castOrderId =
          orderId instanceof CubiqTS.Long
            ? orderId.toString()
            : typeof orderId === "string"
              ? orderId
              : null;
        if (this.base.hasItem(castOrderId)) {
          /* ORDER DATA */
          let orderDataStored: BmarkOrderData = this.base.getBmarkOrderDataForOrderId(castOrderId);
          logger.out.info(
            `BMARK: Order state (${castOrderId}) update to ${
              CubiqTS.OrderState[orderDataStored.orderState]
            }`,
          );
          /* EVENT */
          let orderDataEvent = new BmarkOrderDataEvent();

          /*************************************/
          /* META-DATA PREPARATION */
          let traderGroupIds: Array<string> = new Array<string>();
          if (orderDataStored.deskId != null) {
            traderGroupIds.push(orderDataStored.deskId.toString());
          }
          if (
            traderGroupIds.length === 0 &&
            Array.isArray(orderDataStored.ownerGroupIds) &&
            orderDataStored.ownerGroupIds[0] instanceof CubiqTS.Long
          ) {
            traderGroupIds = orderDataStored.ownerGroupIds.map((item) => item.toString());
          }

          /*************************************/
          /* DEFINE EVENT */
          /* define meta data */
          let eventPayloadData = new BmarkOrderDataEventPayload(
            orderDataStored.orderId.toString(),
            orderDataStored.clOrdID,
            BmarkOrderDataEventSource.OrderLifeCycleEvent,
            orderDataStored,
          );
          // bmark order data
          eventPayloadData.bmarkOrderData = orderDataStored;

          eventPayloadData.state = orderDataStored.orderState;
          eventPayloadData.evtType = CubiqTS.FxLifeCycleEventType.FILL_EXPIRED;
          eventPayloadData.messageId = fixingEvent.messageId;
          eventPayloadData.tgcId = traderGroupIds;
          (eventPayloadData.sgcId =
            orderDataStored.salesGroupId != null ? orderDataStored.salesGroupId.toString() : null),
            (eventPayloadData.deskCode =
              Array.isArray(traderGroupIds) && traderGroupIds.length > 0
                ? traderGroupIds.map((item) => {
                    let tgc = this.traderGroupService.getItem(item.toString());
                    return tgc instanceof CubiqTS.TradingGroupImpl
                      ? tgc.groupCode
                      : item.toString();
                  })
                : new Array<string>());

          /* define operations */
          orderDataEvent.addOperations(BmarkOrderDataEventOperation.UPDATE);

          /* attach payload */
          orderDataEvent.payload = eventPayloadData;

          orderDataUpdateForFixings.push(orderDataEvent.payload.bmarkOrderData);

          /* FIRE BMARK EVENT */
          this.fireBmarkOrderDataUpdateEvent(orderDataEvent);
        } else {
          logger.out.error(
            `ERROR: BMARK: processFixingCompleteEventForExpiredOrders() Order not found: ${castOrderId}`,
          );
        }
      }

      orderDataUpdateForFixings.forEach((orderData: BmarkOrderData) => {
        this.processOrderDataUpdateForFixings(orderData);
      });
    }
  }

  /* ******************************************************* */
  /* NOTIFICATIONS */

  // OrderDealState: ACTIVE, FILL_ERROR, COMPLETED, CANCELLED, ACK_TIME_OUT, NAK, DONE_NO_STP, STP_HOLD,PENDING

  /* BMARK FILL NOTIFICATION */
  private dispatchBmarkFillNotification(deal: CubiqTS.DealImpl): void {
    if (deal instanceof CubiqTS.DealImpl) {
      this.dealPrivatizationService.checkPrivatization(deal);

      if (!BENCHMARK_DEAL_FAILURE_STATE.has(deal.dealStateId)) {
        // DEAL FILLED
        let notifType: BFXNotificationType = BFXNotificationType.BMARK_FILLED;
        let notifSubType: BFXNotificationSubType =
          deal.fillState === CubiqTS.FillState.FILLED
            ? BFXNotificationSubType.FILL_FULL
            : deal.fillState === CubiqTS.FillState.PARTFILLED
              ? BFXNotificationSubType.FILL_PART
              : null;
        let appNotifType: BFXAppNotificationType = BFXAppNotificationType.APP_BMARK_FILLED;
        if (
          this.notificationService.shouldNotify(notifType, deal.orderType) &&
          (deal.dealStateId === CubiqTS.OrderDealState.ACTIVE ||
            deal.dealStateId === CubiqTS.OrderDealState.DONE_NO_STP)
        ) {
          this.notificationService.fireNotificationEvent(
            this.notificationService.tradeDataToNotification(deal, notifType, notifSubType),
            BFXNotificationEventType.TRADE_UPDATE_EVENT,
          );
        }

        if (
          this.notificationService.shouldAppNotify(appNotifType, deal.orderType) &&
          (deal.dealStateId === CubiqTS.OrderDealState.ACTIVE ||
            deal.dealStateId === CubiqTS.OrderDealState.DONE_NO_STP)
        ) {
          let data = this.notificationService.tradeDataToNotification(
            deal,
            notifType,
            notifSubType,
          );
          //logger.out.warn(`INFO: ${this.constructor.name}.dispatchBmarkFillNotification() => [${data.tradeNotifData.orderId + ' - ' + data.tradeNotifData.dealState}] `, data);
          this.notificationService.fireAppNotificationEvent(
            data,
            BFXAppNotificationEventType.DEAL_UPDATE_EVENT,
          );
        }
      } else {
        // DEAL FILL FAILED
        let notifType: BFXNotificationType = BFXNotificationType.BMARK_DEAL_FAILURE;
        let notifSubType: BFXNotificationSubType =
          deal.fillState === CubiqTS.FillState.FILLED
            ? BFXNotificationSubType.FILL_FULL
            : deal.fillState === CubiqTS.FillState.PARTFILLED
              ? BFXNotificationSubType.FILL_PART
              : null;
        if (
          this.notificationService.shouldNotify(notifType, deal.orderType) &&
          deal.fillState != CubiqTS.FillState.FILLED
        ) {
          this.notificationService.fireNotificationEvent(
            this.notificationService.tradeDataToNotification(deal, notifType, notifSubType),
            BFXNotificationEventType.TRADE_UPDATE_EVENT,
          );
        }
      }
    }
  }

  /* BMARK FIXING UPDATE NOTIFICATION */
  private dispatchBmarkFixingUpdateNotification(
    fixingData: BmarkFixingData,
    fixingEvent: CubiqTS.FixingEventImpl,
    fixingType: BmarkFixingType,
  ) {
    try {
      let sentFixingId =
        fixingData.id.indexOf(BmarkFixingType[fixingType]) >= 0
          ? fixingData.id.replace(`:${BmarkFixingType[fixingType]}`, "")
          : fixingData.id.indexOf(":ACTIVE") >= 0
            ? fixingData.id.replace(":ACTIVE", "")
            : fixingData.id.indexOf(":RECENT") >= 0
              ? fixingData.id.replace(":RECENT", "")
              : null;
      if (typeof sentFixingId === "string") {
        let determinedNotifTypes = this.notificationProcessor.determineBmarkNotifEvent(fixingEvent);
        let notifType: BFXNotificationType = determinedNotifTypes.type;
        let notifSubType: BFXNotificationSubType = determinedNotifTypes.subType;
        //let appNotifType: BFXAppNotificationType = determinedNotifTypes.appType;
        //
        let shouldNotifyFg: boolean = this.notificationService.shouldNotify(notifType);
        //
        let isEventSCOState = fixingEvent.fixingState === CubiqTS.FixingState.SOFT_CUTOFF;
        let isEventHCOState = fixingEvent.fixingState === CubiqTS.FixingState.HARD_CUTOFF;
        let isEventTRIGGERState = fixingEvent.fixingState === CubiqTS.FixingState.TRIGGERED;
        let isValidNotificationState: boolean =
          isEventSCOState || isEventHCOState || isEventTRIGGERState;
        //

        if (isValidNotificationState) {
          if (this.shouldBePurgedOffset(fixingData.fixingTimestamp)) {
            if (this.fixingNotificationsLastSentStateMap.has(sentFixingId)) {
              this.fixingNotificationsLastSentStateMap.delete(sentFixingId);
              this.notificationService.fireNotificationClearEvent(
                this.notificationService.dataToClearEvent([sentFixingId]),
                BFXNotificationEventType.BMARK_FIXING_UPDATE_EVENT,
              );
              //logger.out.warn(`DEV-NOTIFICATIONS: Clearing ${fixingData.id} due to PURGE`);
            }
          } else if (shouldNotifyFg) {
            let doNotSend: boolean = false;
            let wasNotificationSent: boolean =
              this.fixingNotificationsLastSentStateMap.has(sentFixingId);
            let lastSentState: BFXNotificationType =
              this.fixingNotificationsLastSentStateMap.get(sentFixingId);
            let isLastSentStateSCO: boolean =
              lastSentState === BFXNotificationType.BMARK_SOFT_CUT_OFF;
            let isLastSentStateHCO: boolean =
              lastSentState === BFXNotificationType.BMARK_HARD_CUT_OFF;
            let isLastSentStateTRIGGERED: boolean =
              lastSentState === BFXNotificationType.BMARK_TRIGGERED;

            // When NOT to SEND SCO notification
            /*
             * Don't fire the notification,
             * when SCO hits for a specific fixing that has a notification already present
             */
            if (isEventSCOState && wasNotificationSent) {
              doNotSend = true;
            }
            // CURRENT == HCO => CLEAR SCO
            if (isEventHCOState && wasNotificationSent) {
              if (isLastSentStateSCO) {
                //logger.out.warn(`DEV-NOTIFICATIONS: Clearing [${BFXNotificationType[lastSentState]}] for ${fixingData.id} due to incoming [${BFXNotificationType[notifType]}] notif`);
                this.notificationService.fireNotificationClearEvent(
                  this.notificationService.dataToClearEvent([sentFixingId]),
                  BFXNotificationEventType.BMARK_FIXING_UPDATE_EVENT,
                );
              } else if (isLastSentStateHCO || isLastSentStateTRIGGERED) {
                doNotSend = true;
              }
            }
            // CURRENT == TRIGGERED => CLEAR HCO || SCO
            if (isEventTRIGGERState && wasNotificationSent) {
              if (isLastSentStateSCO || isLastSentStateHCO) {
                //logger.out.warn(`DEV-NOTIFICATIONS: Clearing [${BFXNotificationType[lastSentState]}] for ${fixingData.id} due to incoming [${BFXNotificationType[notifType]}] notif`);
                this.notificationService.fireNotificationClearEvent(
                  this.notificationService.dataToClearEvent([sentFixingId]),
                  BFXNotificationEventType.BMARK_FIXING_UPDATE_EVENT,
                );
              } else if (isLastSentStateTRIGGERED) {
                doNotSend = true;
              }
            }

            // SEND
            if (!doNotSend) {
              let bmOrderDataArray = this.base.getBmarkOrderDataForFixingId(
                fixingData.id,
                fixingType,
              );
              //logger.out.warn(`DEV-NOTIFICATIONS: Firing [${BFXNotificationType[notifType]}] for ${fixingData.id} with ${bmOrderDataArray.length} orders`);
              this.fixingNotificationsLastSentStateMap.set(sentFixingId, notifType);
              this.notificationService.fireNotificationEvent(
                this.notificationService.bmarkDataToNotification(
                  sentFixingId,
                  fixingData,
                  fixingEvent,
                  bmOrderDataArray,
                  notifType,
                  notifSubType,
                ),
                BFXNotificationEventType.BMARK_FIXING_UPDATE_EVENT,
              );
            }
          }
        }
      } else {
        logger.out.error(
          `ERROR: ${this.constructor.name}.dispatchBmarkFixingUpdateNotification(): Invalid fixingId = ${sentFixingId}`,
        );
      }
    } catch (ex) {
      logger.out.error(
        `ERROR: ${this.constructor.name}.dispatchBmarkFixingUpdateNotification(): Exception: ${ex}`,
      );
    }
  }

  /* ******************************************************* */
  /* EVENT QUEUING */

  /* FIXING EVENTS */
  protected flushQueuedFixingUpdateEvents(): void {
    this.control.queueFixingEvents = false;
    this.logBmarkInfo(`Fixing event queueing STOPPED! Queue size: ${this.fixingEventQueue.length}`);
    if (this.fixingEventQueue && this.fixingEventQueue.length > 0) {
      this.logBmarkInfo(`Flushing ${this.fixingEventQueue.length} queued fixing events.`);
      this.fixingEventQueue.forEach((queuedEvent: QueuedEvent) => {
        try {
          if (queuedEvent instanceof QueuedEvent) {
            queuedEvent.processEventFunction(queuedEvent.event);
          }
        } catch (error) {
          logger.out.error("Failed to process queued fixing event", queuedEvent, error);
        }
      });
      this.fixingEventQueue = [];
    }
  }

  /* ORDER DATA EVENTS */
  protected flushQueuedBmarkOrderDataEvents(): void {
    this.control.queueOrderDataEvents = false;
    this.logBmarkInfo(
      `Bmark Order Data event queueing STOPPED! Queue size: ${this.orderDataEventQueue.length}`,
    );
    if (Array.isArray(this.orderDataEventQueue) && this.orderDataEventQueue.length > 0) {
      this.logBmarkInfo(
        `Flushing ${this.orderDataEventQueue.length} queued Bmark Order Data events.`,
        null,
        true,
      );
      this.orderDataEventQueue.forEach((queuedEvent: QueuedEvent) => {
        try {
          this.logBmarkInfo(
            `Processing Queued ORDER DATA Event for fixing ${BmarkFixingData.generateFixingDataLogMsg(
              queuedEvent.event.orderData,
            )}`,
            null,
            true,
          );
          queuedEvent.processEventFunction(queuedEvent.event);
        } catch (error) {
          logger.out.error("Failed to process queued order data update event", queuedEvent, error);
        }
      });
      this.orderDataEventQueue = [];
    }
  }

  /* ******************************************************* */
  /* ORDER DATA EVENT HANDLER HELPERS */
  protected shouldProcessOutsidePreVisibility(
    order: CubiqTS.OrderDataImpl,
    lifecycleEvent: CubiqTS.OrderLifeCycleEventImpl = null,
  ): boolean {
    if (
      order != null &&
      this.activeUser != null &&
      order.insidePrevisibilityFg != null &&
      order.insidePrevisibilityFg === false &&
      !this.activeUser.preVisibilityVisible &&
      this.activeUser.isTrader() &&
      this.activeUser.userId != null &&
      order.entryUserId != null &&
      this.activeUser.userId.toString() != order.entryUserId.toString()
    ) {
      let oldItem: CubiqTS.OrderDataImpl = this.base.getItem(this.base.getItemKey(order));

      if (oldItem != null && order.txTime.isSameOrBefore(oldItem.txTime)) {
        logger.out.info(
          `${this.constructor.name}: Order ${order.orderRef} ignoring outside pre-fixing visibility as txTime is same or before (rollback).`,
        );
        return false;
      }

      logger.out.info(
        `${this.constructor.name}: Order ${order.orderRef} is outside pre-fixing visibility. User does not have privilege.`,
      );
      return true;
    }

    return false;
  }

  protected shouldProcessUpdate(
    order: CubiqTS.OrderDataImpl,
    lifecycleEvent: CubiqTS.OrderLifeCycleEventImpl = null,
  ): boolean {
    try {
      if (Array.isArray(order.allocationData) && order.allocationData.length > 1) {
        console.log(`${this.constructor.name}.shouldProcessUpdate() order: %o`, order);
        console.log(`${this.constructor.name}.shouldProcessUpdate() lifecycle: %o`, lifecycleEvent);
      }

      if (this.shouldProcessOutsidePreVisibility(order, lifecycleEvent)) {
        return false;
      }

      let key = this.base.getItemKey(order);
      let oldItem: CubiqTS.OrderDataImpl = this.base.getItem(key);
      let oldTxTime: number = this.base.getJustDeletedTime(key);
      if (oldItem != null && order != null && order.txTime != null && oldItem.txTime != null) {
        let ret: boolean = order.txTime.isAfter(oldItem.txTime);

        // If it's a rollback event and the transaction time is the same of the new and existing data than we process it
        if (
          !ret &&
          this.isRollbackLifeCycleEvent(lifecycleEvent) &&
          order.txTime.isSame(oldItem.txTime)
        ) {
          ret = true;
        }

        if (!ret && this.isSameTimeLifeCycleEvent(lifecycleEvent)) {
          logger.out.info("procesing SAME_TIME event: ", lifecycleEvent);
          ret = true;
        }

        if (!ret && order.orderId != null) {
          logger.out.warn(
            `${
              this.constructor.name
            }: Skipping order data update for order ${order.orderId.toString()} message ID ${
              order.messageId
            }.`,
          );
        }

        return ret;
      } else if (order != null && order.txTime != null && typeof oldTxTime === "number") {
        let newTxTime = parseInt(order.txTime.format("x"));
        let ret: boolean = newTxTime > oldTxTime;

        // If it's a rollback event and the transaction time is the same of the new and existing data than we process it
        if (
          !ret &&
          lifecycleEvent != null &&
          lifecycleEvent.rollbackEventFg === true &&
          newTxTime == oldTxTime
        ) {
          ret = true;
        }

        return ret;
      }
    } catch (error) {
      logger.out.warn(
        "Error determining if an order update should be processed. Update will be processed.",
        order,
        error,
      );
    }
    return order != null;
  }
  isRollbackLifeCycleEvent(lifecycleEvent: CubiqTS.OrderLifeCycleEventImpl): boolean {
    return lifecycleEvent != null && lifecycleEvent.rollbackEventFg === true;
  }
  isSameTimeLifeCycleEvent(lifecycleEvent: CubiqTS.OrderLifeCycleEventImpl): boolean {
    return (
      lifecycleEvent != null &&
      lifecycleEvent.extendedInfo != null &&
      lifecycleEvent.extendedInfo.has(EXTINFO_SAME_TIME_PROCESSING) &&
      lifecycleEvent.extendedInfo.get(EXTINFO_SAME_TIME_PROCESSING) === BOOLEAN_STR_TRUE
    );
  }

  protected shouldProcessUnsoLCancelUpdate(order: CubiqTS.OrderDataImpl): boolean {
    try {
      if (
        order != null &&
        this.activeUser != null &&
        order.insidePrevisibilityFg != null &&
        order.insidePrevisibilityFg === false &&
        !this.activeUser.preVisibilityVisible &&
        this.activeUser.isTrader() &&
        this.activeUser.userId != null &&
        order.entryUserId != null &&
        this.activeUser.userId.toString() != order.entryUserId.toString()
      ) {
        logger.out.info(
          `${this.constructor.name}: Order ${order.orderRef} outside pre-fixing visibility. User does not have privilege.`,
        );
        return false;
      }
    } catch (error) {
      logger.out.warn(
        "Error determining if an order update should be processed. Update will be processed.",
        order,
        error,
      );
    }
  }

  /* ******************************************************* */
  /*  NOTIFICATION TYPE HANDLING */

  public getAllAllocationDatasetForOrder(
    orderData: CubiqTS.OrderDataImpl | BmarkOrderData,
  ): Set<CubiqTS.AllocationData> {
    return this.base.getAllAllocationDatasetForOrder(orderData);
  }

  /* ******************************************************* */
  /*  DEBUG - LOG */

  protected logBmarkInfo(
    message: string,
    dumpData: any = null,
    warning: boolean = false,
    error: boolean = false,
    feature: string = "BMARK",
  ): void {
    try {
      if (this.outputLog === false) {
        dumpData = null;
      }

      let prefix = `${this.constructor.name} ${error ? "ERROR: " : warning ? "WARN: " : ":"}`;
      if (error) {
        if (dumpData != null) {
          logger.out.error(`${prefix} ${typeof message === "string" ? message : ""} %O`, dumpData);
        } else {
          logger.out.error(`${prefix} ${typeof message === "string" ? message : ""}`);
        }
      } else if (warning) {
        if (dumpData != null) {
          logger.out.warn(`${prefix} ${typeof message === "string" ? message : ""} %O`, dumpData);
        } else {
          logger.out.warn(`${prefix} ${typeof message === "string" ? message : ""}`);
        }
      } else {
        if (dumpData != null) {
          logger.out.info(`${prefix} ${typeof message === "string" ? message : ""} %O`, dumpData);
        } else {
          logger.out.info(`${prefix} ${typeof message === "string" ? message : ""}`);
        }
      }
    } catch (ex) {
      logger.out.error(`WORKER-ERROR: Extended logging exception (Other): %O`, ex);
    }
  }

  public getActiveFixingData(fixingId: string): BmarkFixingData {
    return this.base.getActiveFixingData(fixingId);
  }

  /**
   * In rare race conditions,( see https://jira.iongroup.com/browse/FXBFXOM-10363), the server may send out events without NPC
   * values immediately after sending out events with NPV values. In this scenario, we need to copy the NPV values from the first event
   * to the OrderData in the second
   * @param storedData
   * @param newData
   * @returns
   */
  private updateMissingNPVValues(storedData: BmarkOrderData, newData: BmarkOrderData) {
    if (
      !storedData?.orderRevisionId ||
      !newData?.orderRevisionId ||
      !storedData.orderRevisionId.equals(newData.orderRevisionId)
    ) {
      return;
    }
    if (storedData.allocationData[0]?.npvAllocationQty) {
      if (!newData.allocationData[0]?.npvAllocationQty) {
        logger.out.info(`bpdl-s - Updating NPV values from old BM order data`);
        // Old has NPV, new doesnt - need to copy over the NPV data
        const storedMap = this.createAllocationMap(storedData);
        const newMap = this.createAllocationMap(newData);
        if (storedMap.size == newMap.size) {
          // We have the same allocations
          for (const allocation of newMap.values()) {
            // Copy NPV values from old allocation to new allocation
            const oldAllocation = storedMap.get(allocation.allocationRef);
            if (oldAllocation) {
              allocation.npvAllocationQty = oldAllocation.npvAllocationQty;
              allocation.npvSettleQty = oldAllocation.npvSettleQty;
            } else {
              logger.out.warn(
                `bdpl-s - Cannot find allocation for reference ${allocation.allocationRef}`,
              );
            }
          }
        } else {
          logger.out.warn(
            `bdpl-s - Not copying NPV amounts for order ${storedData.orderId}, allocation counts do not match - ${storedMap.size} vs '${newMap.size}`,
          );
        }
      }
    }
  }

  /**
   * Return a map of allocationRef vs Allocation for all allocations (all types)
   * @param order
   * @returns
   */
  private createAllocationMap(order: BmarkOrderData): Map<string, CubiqTS.AllocationDataImpl> {
    const retMap: Map<string, CubiqTS.AllocationDataImpl> = new Map();

    for (const allocation of order.allocationData) {
      retMap.set(allocation.allocationRef, allocation);
      if (allocation.componentAllocations) {
        for (const cross of allocation.componentAllocations) {
          retMap.set(cross.allocationRef, cross);
        }
      }
    }
    return retMap;
  }
}
